import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { 
  Box, Grid, Paper, Chip, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Avatar, Stack, 
  LinearProgress, Fab, Typography, Dialog, DialogTitle, IconButton, Alert, Snackbar, 
  Menu, Skeleton, useTheme, useMediaQuery
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Bolt as BoltIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  BackupTable as BacklogIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import { 
  DashboardLayout,
  CreateTicketModal,
  SprintManagement,
  Board,
  Settings,
  AllTicketsView,
  ErrorBoundary
} from "./components/index.js";
import { handleApiError, getErrorMessage, getErrorSeverity, logError } from "./utils/index.js";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';


// ========================================
// CONFIGURATION: Real API integration with Go backend
// ========================================
// API endpoints:
// - GET /api/tickets - Returns array of ticket objects
// - GET /api/sprints - Returns array of sprint objects  
// - GET /api/team-members - Returns array of team member objects
// - GET /api/trends - Returns { daily: [...], weekly: [...] } trend data
// ========================================

// Import the new API service and configuration
import { apiService as api } from './services/index.js';
import { initConfig } from './constants/environment.js';

// Initialize environment configuration
const { config, warnings } = initConfig();

// API base URL from environment configuration
const API_BASE_URL = config.api.baseUrl;

// API service functions - now using the centralized API service
const apiService = {
  async fetchTickets() {
    try {
      return await api.tickets.getAll();
    } catch (error) {
      logError(error, 'fetchTickets');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async fetchSprints() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sprints`);
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      return await response.json();
    } catch (error) {
      logError(error, 'fetchSprints');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async fetchTeamMembers() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team-members`);
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      return await response.json();
    } catch (error) {
      logError(error, 'fetchTeamMembers');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async fetchTrends() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trends`);
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      return await response.json();
    } catch (error) {
      logError(error, 'fetchTrends');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async createTicket(ticketData) {
    try {
      // The ticketData is already properly formatted by handleCreateTicket
      // Just pass it directly to the API
      return await api.tickets.create(ticketData);
    } catch (error) {
      logError(error, 'createTicket');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async updateTicket(id, ticketData) {
    try {
      return await api.tickets.update(id, ticketData);
    } catch (error) {
      logError(error, 'updateTicket');
      const appError = handleApiError(error);
      throw appError;
    }
  },

  async deleteTicket(id) {
    try {
      return await api.tickets.delete(id);
    } catch (error) {
      logError(error, 'deleteTicket');
      const appError = handleApiError(error);
      throw appError;
    }
  }
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// ---------------- Mock Data (Fallback) ----------------
// These are now fallback data when API calls fail


// Enhanced mock data for insights
const mockTrendData = {
  daily: [
    { date: '2025-08-27', todo: 12, inProgress: 8, done: 15, blocked: 2, tickets: 15, bugs: 8, stories: 12, epics: 3, subtasks: 5, completed: 15, created: 12, velocity: 8 },
    { date: '2025-08-22', todo: 14, inProgress: 9, done: 16, blocked: 1, tickets: 16, bugs: 9, stories: 14, epics: 2, subtasks: 6, completed: 16, created: 14, velocity: 9 },
    { date: '2025-08-17', todo: 11, inProgress: 10, done: 18, blocked: 3, tickets: 18, bugs: 10, stories: 11, epics: 4, subtasks: 7, completed: 18, created: 11, velocity: 10 },
    { date: '2025-08-12', todo: 13, inProgress: 7, done: 20, blocked: 2, tickets: 20, bugs: 7, stories: 13, epics: 3, subtasks: 8, completed: 20, created: 13, velocity: 7 },
    { date: '2025-08-07', todo: 10, inProgress: 11, done: 22, blocked: 1, tickets: 22, bugs: 11, stories: 10, epics: 2, subtasks: 9, completed: 22, created: 10, velocity: 11 },
    { date: '2025-08-02', todo: 9, inProgress: 12, done: 25, blocked: 2, tickets: 25, bugs: 12, stories: 9, epics: 3, subtasks: 10, completed: 25, created: 9, velocity: 12 },
    { date: '2025-07-28', todo: 8, inProgress: 13, done: 28, blocked: 1, tickets: 28, bugs: 13, stories: 8, epics: 2, subtasks: 11, completed: 28, created: 8, velocity: 13 }
  ],
  weekly: [
    { week: 'Week 1', todo: 85, inProgress: 65, done: 120, blocked: 15, tickets: 120, bugs: 65, stories: 85, epics: 20, subtasks: 30, completed: 120, created: 85, velocity: 65 },
    { week: 'Week 2', todo: 78, inProgress: 72, done: 135, blocked: 12, tickets: 135, bugs: 72, stories: 78, epics: 18, subtasks: 35, completed: 135, created: 78, velocity: 72 },
    { week: 'Week 3', todo: 72, inProgress: 78, done: 150, blocked: 10, tickets: 150, bugs: 78, stories: 72, epics: 15, subtasks: 40, completed: 150, created: 72, velocity: 78 },
    { week: 'Week 4', todo: 65, inProgress: 85, done: 165, blocked: 8, tickets: 165, bugs: 85, stories: 65, epics: 12, subtasks: 45, completed: 165, created: 65, velocity: 85 },
    { week: 'Week 5', todo: 58, inProgress: 92, done: 180, blocked: 6, tickets: 180, bugs: 92, stories: 58, epics: 10, subtasks: 50, completed: 180, created: 58, velocity: 92 },
    { week: 'Week 6', todo: 52, inProgress: 98, done: 195, blocked: 5, tickets: 195, bugs: 98, stories: 52, epics: 8, subtasks: 55, completed: 195, created: 52, velocity: 98 }
  ]
};

// Mock data for team members
const mockMembers = [
  { id: 1, name: 'Sarah K.', avatar: 'S', totalAssigned: 12, todo: 3, inProgress: 5, done: 4, timeSpent: '32h', occupancy: '85%' },
  { id: 2, name: 'Alex M.', avatar: 'A', totalAssigned: 8, todo: 2, inProgress: 3, done: 3, timeSpent: '24h', occupancy: '70%' },
  { id: 3, name: 'Kevin L.', avatar: 'K', totalAssigned: 15, todo: 4, inProgress: 6, done: 5, timeSpent: '40h', occupancy: '90%' },
  { id: 4, name: 'Yuki T.', avatar: 'YT', totalAssigned: 10, todo: 2, inProgress: 4, done: 4, timeSpent: '28h', occupancy: '75%' }
];

// Mock data for sprints
const mockSprints = [
  { id: 1, name: 'Sprint 1', start: '2025-01-15', end: '2025-01-28', tickets: 12, progress: 0.8, active: true, status: 'ACTIVE' },
  { id: 2, name: 'Sprint 2', start: '2025-02-01', end: '2025-02-14', tickets: 8, progress: 0.4, active: false, status: 'FUTURE' },
  { id: 3, name: 'Sprint 3', start: '2024-12-15', end: '2024-12-28', tickets: 15, progress: 1.0, active: false, status: 'COMPLETED' },
  { id: 4, name: 'Sprint 4', start: '2025-01-01', end: '2025-01-14', tickets: 10, progress: 0.9, active: false, status: 'COMPLETED' }
];

// Mock data for epics
const mockEpics = [
  { id: 1, name: 'Security Enhancement', description: 'Improve application security' },
  { id: 2, name: 'User Management', description: 'Enhanced user management features' },
  { id: 3, name: 'UI/UX Improvements', description: 'Better user interface and experience' }
];

// Mock data for labels
const mockLabels = [
  { id: 1, name: 'frontend', color: '#3B82F6' },
  { id: 2, name: 'backend', color: '#10B981' },
  { id: 3, name: 'security', color: '#EF4444' },
  { id: 4, name: 'bug', color: '#F59E0B' },
  { id: 5, name: 'feature', color: '#8B5CF6' }
];

// ---------------- Kanban Board Data ----------------
// Utility function for generating unique IDs
const generateId = () => `ticket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Enhanced mock data for Kanban board
const initialTickets = [
  {
    id: 'ticket-1',
    title: 'Implement user authentication',
    type: 'task',
    status: 'todo',
    assignee: 'YT',
    assigneeName: 'Yuki Tanaka',
    priority: 'high',
    labels: ['frontend', 'auth'],
    epic: 'user-mgmt',
    createdAt: new Date('2023-06-15'),
    updatedAt: new Date('2023-06-15')
  },
  {
    id: 'ticket-2',
    title: 'Fix login validation bug',
    type: 'bug',
    status: 'todo',
    assignee: 'MJ',
    assigneeName: 'Michael Johnson',
    priority: 'medium',
    labels: ['bug', 'auth'],
    epic: 'user-mgmt',
    createdAt: new Date('2023-06-18'),
    updatedAt: new Date('2023-06-18')
  },
  {
    id: 'ticket-3',
    title: 'Design dashboard UI',
    type: 'task',
    status: 'inProgress',
    assignee: 'SD',
    assigneeName: 'Sarah Davis',
    priority: 'high',
    labels: ['design', 'ui'],
    epic: 'dashboard',
    createdAt: new Date('2023-06-10'),
    updatedAt: new Date('2023-06-20')
  },
  {
    id: 'ticket-4',
    title: 'API endpoint for user data',
    type: 'story',
    status: 'done',
    assignee: 'RP',
    assigneeName: 'Robert Patel',
    priority: 'medium',
    labels: ['backend', 'api'],
    epic: 'user-mgmt',
    createdAt: new Date('2023-05-15'),
    updatedAt: new Date('2023-06-01'),
    completedAt: new Date('2023-06-01')
  },
  {
    id: 'ticket-5',
    title: 'Create user profile page',
    type: 'task',
    status: 'todo',
    assignee: 'YT',
    assigneeName: 'Yuki Tanaka',
    priority: 'low',
    labels: ['frontend', 'profile'],
    epic: 'user-mgmt',
    createdAt: new Date('2023-06-20'),
    updatedAt: new Date('2023-06-20')
  }
];

const epics = [
  { id: 'user-mgmt', name: 'User Management' },
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'performance', name: 'Performance' },
  { id: 'checkout', name: 'Checkout' },
  { id: 'settings', name: 'Settings' }
];

const labels = [
  { id: 'frontend', name: 'Frontend', color: '#70b500' },
  { id: 'backend', name: 'Backend', color: '#e11d21' },
  { id: 'ui', name: 'UI/UX', color: '#0052cc' },
  { id: 'bug', name: 'Bug', color: '#e11d21' },
  { id: 'auth', name: 'Authentication', color: '#006b75' },
  { id: 'api', name: 'API', color: '#0052cc' },
  { id: 'database', name: 'Database', color: '#5319e7' },
  { id: 'responsive', name: 'Responsive', color: '#006b75' },
  { id: 'payment', name: 'Payment', color: '#0052cc' },
  { id: 'performance', name: 'Performance', color: '#5319e7' }
];

const assignees = [
  { id: 'YT', name: 'Yuki Tanaka', avatar: 'YT' },
  { id: 'MJ', name: 'Michael Johnson', avatar: 'MJ' },
  { id: 'SD', name: 'Sarah Davis', avatar: 'SD' },
  { id: 'RP', name: 'Robert Patel', avatar: 'RP' },
  { id: 'AJ', name: 'Alex Johnson', avatar: 'AJ' }
];

const columns = [
  { id: 'todo', title: 'To Do', color: '#f5f5dc' },
  { id: 'inProgress', title: 'In Progress', color: '#e1f5fe' },
  { id: 'done', title: 'Done', color: '#e8f5e9' }
];

// ---------------- Helpers ----------------

function AssigneeAvatars({ users }) {
  return (
    <Stack direction="row" spacing={-0.5} sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 12, border: "1px solid #e5e7eb" } }}>
      {users.map((u) => (
        <Avatar key={u}>{u}</Avatar>
      ))}
    </Stack>
  );
}

function FiltersBar() {
  const [assignee, setAssignee] = useState("All");
  const [status, setStatus] = useState("All");
  
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ 
      '& .MuiFormControl-root': { 
        minWidth: { xs: '100%', sm: 140, md: 160 } 
      },
      '& .MuiTextField-root': { 
        minWidth: { xs: '100%', sm: 140, md: 160 } 
      }
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: 1, borderColor: "divider", borderRadius: 2, px: 1.25, minWidth: { xs: '100%', sm: 200, md: 240 } }}>
        <SearchIcon fontSize="small" />
        <TextField size="small" variant="standard" placeholder="Search" InputProps={{ disableUnderline: true }} />
      </Box>
      <TextField size="small" type="date" />
      <Typography variant="body2">—</Typography>
      <TextField size="small" type="date" />
      <FormControl size="small">
        <InputLabel>Assignee</InputLabel>
        <Select label="Assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="A">A</MenuItem>
          <MenuItem value="K">K</MenuItem>
          <MenuItem value="S">S</MenuItem>
          <MenuItem value="YT">YT</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small">
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="todo">Todo</MenuItem>
          <MenuItem value="inprogress">In Progress</MenuItem>
          <MenuItem value="done">Done</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small">
        <InputLabel>Type</InputLabel>
        <Select label="Type" defaultValue="All">
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="task">Task</MenuItem>
          <MenuItem value="bug">Bug</MenuItem>
          <MenuItem value="story">Story</MenuItem>
        </Select>
      </FormControl>
             <Button 
         startIcon={<CalendarIcon />} 
         variant="outlined" 
         size="small"
         sx={{ 
           ml: "auto", 
           minWidth: { xs: '100%', sm: 'auto' },
           px: 1.5,
           py: 0.5
         }}
       >
         Last 30 days
       </Button>
    </Stack>
  );
}

// ---------------- Kanban Board Components ----------------
// Kanban Board Components moved to Board.jsx

// Date Range Picker Component
const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, onClose }) => {
  return (
    <Box sx={{ p: 2, minWidth: 300 }}>
      <Stack spacing={2}>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={onStartDateChange}
          renderInput={(params) => <TextField {...params} />}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={onEndDateChange}
          renderInput={(params) => <TextField {...params} />}
        />
        <Button variant="contained" onClick={onClose}>Apply</Button>
      </Stack>
    </Box>
  );
};

// ---------------- Enhanced Chart Components ----------------
  // Main TrendChart component for ticket type trends
  function TrendChart({ title, data, type = "line", height = 300 }) {
    const [chartType, setChartType] = useState("daily");
    const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
    
    // Add comprehensive safety check for empty data
    if (!data || !data[chartType] || !Array.isArray(data[chartType]) || data[chartType].length === 0) {
      return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
          <Typography variant="h6" fontWeight={600}>{title}</Typography>
          <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        </Paper>
      );
    }
    
    const chartData = data[chartType];
    
    // Additional validation to ensure chartData has the required properties
    if (!chartData.every(d => d && typeof d === 'object')) {
      return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
          <Typography variant="h6" fontWeight={600}>{title}</Typography>
          <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Invalid data format</Typography>
          </Box>
        </Paper>
      );
    }
    
    const dailyChartData = {
      labels: chartData.map(d => {
        const date = new Date(d.date);
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'Tickets',
          data: chartData.map(d => d.tickets),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Bugs',
          data: chartData.map(d => d.bugs),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Stories',
          data: chartData.map(d => d.stories),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Epics',
          data: chartData.map(d => d.epics),
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Subtasks',
          data: chartData.map(d => d.subtasks),
          borderColor: 'rgb(245, 158, 11)',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: true,
          tension: 0.4,
        }
      ]
    };

    const weeklyChartData = {
      labels: chartData.map(w => w.week),
      datasets: [
        {
          label: 'Completed',
          data: chartData.map(w => w.completed),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Created',
          data: chartData.map(w => w.created),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Velocity',
          data: chartData.map(w => w.velocity),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
        }
      ]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12
            },
            generateLabels: function(chart) {
              const data = chart.data;
              if (data.labels.length === 0) return [];
              
              return data.datasets.map((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                if (!meta.visible) return null;
                
                return {
                  text: dataset.label,
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: 2,
                  hidden: !meta.visible,
                  index: i
                };
              }).filter(label => label !== null);
            },
            onClick: function(e, legendItem, legend) {
              try {
                const index = legendItem.index;
                const ci = legend.chart;
                
                // Prevent the default Chart.js legend click behavior
                e.preventDefault();
                e.stopPropagation();
                
                if (ci && ci.data && ci.data.datasets && ci.data.datasets[index]) {
                  const meta = ci.getDatasetMeta(index);
                  if (meta) {
                    // Toggle visibility manually
                    if (meta.visible) {
                      meta.visible = false;
                      legendItem.hidden = true;
                      setHiddenDatasets(prev => new Set([...prev, index]));
                    } else {
                      meta.visible = true;
                      legendItem.hidden = false;
                      setHiddenDatasets(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(index);
                        return newSet;
                      });
                    }
                    
                    // Update the chart safely
                    try {
                      ci.update('none');
                    } catch (updateError) {
                      console.warn('Chart update error:', updateError);
                    }
                  }
                }
              } catch (error) {
                console.warn('Legend click error:', error);
              }
            }
          },
          onClick: function(e, legendItem, legend) {
            try {
              const index = legendItem.index;
              const ci = legend.chart;
              
              if (ci && typeof ci.isDatasetVisible === 'function') {
                // Toggle visibility
                if (ci.isDatasetVisible(index)) {
                  ci.hide(index);
                  legendItem.hidden = true;
                  // Update local state
                  setHiddenDatasets(prev => new Set([...prev, index]));
                } else {
                  ci.show(index);
                  legendItem.hidden = false;
                  // Update local state
                  setHiddenDatasets(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(index);
                    return newSet;
                  });
                }
                
                // Update the chart with proper animation
                ci.update('active');
              }
            } catch (error) {
              console.warn('Legend click error:', error);
            }
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          position: 'nearest',
          xAlign: 'auto',
          yAlign: 'auto',
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
            label: function(context) {
              const dataset = context.dataset;
              const value = context.parsed.y;
              const label = dataset.label;
              
              // Show actual data values with proper formatting
              if (chartType === "daily") {
                const date = new Date(chartData[context.dataIndex].date);
                return `${label}: ${value}`;
              } else {
                return `${label}: ${value}`;
              }
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };

    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>{title}</Typography>
          <Stack direction="row" spacing={1}>
            <Button 
              size="small" 
              variant={chartType === "daily" ? "contained" : "outlined"}
              onClick={() => setChartType("daily")}
            >
              Daily
            </Button>
            <Button 
              size="small" 
              variant={chartType === "weekly" ? "contained" : "outlined"}
              onClick={() => setChartType("weekly")}
            >
              Weekly
            </Button>
          </Stack>
        </Stack>
        
        <Box sx={{ height: height, position: "relative", width: '100%', '& canvas': { width: '100% !important' } }}>
          {(() => {
            try {
              return chartType === "daily" ? (
                <Line data={dailyChartData} options={chartOptions} />
              ) : (
                <Line data={weeklyChartData} options={chartOptions} />
              );
            } catch (error) {
              console.error('Chart rendering error:', error);
              return (
                <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="error.main">Chart rendering error</Typography>
                </Box>
              );
            }
          })()}
        </Box>
      </Paper>
    );
}

// ---------------- Ticket Details Modal ----------------
function TicketDetailsModal({ open, onClose, title, count, tickets }) {
  const [filters, setFilters] = useState({
    dateRange: { start: '2025-07-22', end: '2025-08-21' },
    assignee: 'All',
    status: 'All',
    ticketType: 'All',
    epic: 'All',
    label: 'All',
    priority: 'All',
    team: 'All',
    reporter: 'All',
    searchTerm: ''
  });

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const getTicketIcon = (type) => {
    switch(type) {
      case 'task': return '';
      case 'bug': return '';
      case 'story': return '';
      case 'epic': return '';
      default: return '';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'todo': return 'warning';
      case 'inprogress': return 'info';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  // Filter tickets based on current filters
  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableText = [
        ticket.id?.toString(),
        ticket.title,
        ticket.summary,
        ticket.description
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }
    
    // Other filters
    if (filters.assignee !== 'All' && ticket.assignees && !ticket.assignees.includes(filters.assignee)) return false;
    if (filters.status !== 'All' && ticket.status !== filters.status) return false;
    if (filters.ticketType !== 'All' && ticket.type !== filters.ticketType) return false;
    if (filters.epic !== 'All' && ticket.epic !== filters.epic) return false;
    if (filters.label !== 'All' && ticket.labels && !ticket.labels.includes(filters.label)) return false;
    if (filters.priority !== 'All' && ticket.priority !== filters.priority) return false;
    if (filters.team !== 'All' && ticket.team !== filters.team) return false;
    if (filters.reporter !== 'All' && ticket.reporter !== filters.reporter) return false;
    return true;
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h5" fontWeight={600}>
          {title} ({filteredTickets.length}{filters.searchTerm ? ` - "${filters.searchTerm}"` : ''})
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Search Bar */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        {/* Search Box */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            bgcolor: 'grey.100',
            maxWidth: 400,
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
            '&:focus-within': { 
              borderColor: 'primary.main', 
              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)' 
            }
          }}>
            <SearchIcon 
              fontSize="small" 
              color="action"
              sx={{ color: 'text.secondary' }}
            />
            <TextField
              size="small"
              variant="standard"
              placeholder="Search"
              InputProps={{ 
                disableUnderline: true,
                sx: { 
                  fontSize: '14px',
                  '& input': { 
                    py: 0.5,
                    color: 'text.primary',
                    '&::placeholder': {
                      color: 'text.disabled',
                      opacity: 1
                    }
                  }
                }
              }}
              value={filters.searchTerm || ''}
              onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              fullWidth
            />
            {filters.searchTerm && (
              <IconButton
                size="small"
                onClick={() => setFilters({...filters, searchTerm: ''})}
                sx={{ p: 0.5, color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          
          {filters.searchTerm && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                Searching for: "{filters.searchTerm}"
              </Typography>
              <Button 
                size="small" 
                variant="text" 
                onClick={() => setFilters({...filters, searchTerm: ''})}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                Clear search
              </Button>
            </Box>
          )}
        </Box>

        {/* Filter Bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          flexWrap: 'wrap',
          mb: 2
        }}>
          {/* Date Range Selector */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            minWidth: 200,
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
          }}>
            <TextField
              size="small"
              type="date"
              value={filters.dateRange.start}
              onChange={(e) => setFilters({...filters, dateRange: { start: e.target.value, end: filters.dateRange.end }})}
              variant="standard"
              InputProps={{ 
                disableUnderline: true,
                sx: { fontSize: '14px' }
              }}
              sx={{ minWidth: 80 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
              →
            </Typography>
            <TextField
              size="small"
              type="date"
              value={filters.dateRange.end}
              onChange={(e) => setFilters({...filters, dateRange: { end: e.target.value, start: filters.dateRange.start }})}
              variant="standard"
              InputProps={{ 
                disableUnderline: true,
                sx: { fontSize: '14px' }
              }}
              sx={{ minWidth: 80 }}
            />

          </Box>

          {/* Dropdown Filters */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.assignee}
              onChange={(e) => setFilters({...filters, assignee: e.target.value})}
              variant="outlined"
              displayEmpty
              sx={{
                height: 40,
                fontSize: '14px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '14px' }}>Assignee: All</MenuItem>
              <MenuItem value="S" sx={{ fontSize: '14px' }}>S</MenuItem>
              <MenuItem value="K" sx={{ fontSize: '14px' }}>K</MenuItem>
              <MenuItem value="A" sx={{ fontSize: '14px' }}>A</MenuItem>
              <MenuItem value="YT" sx={{ fontSize: '14px' }}>YT</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              variant="outlined"
              displayEmpty
              sx={{
                height: 40,
                fontSize: '14px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '14px' }}>Status: All</MenuItem>
              <MenuItem value="todo" sx={{ fontSize: '14px' }}>TODO</MenuItem>
              <MenuItem value="inprogress" sx={{ fontSize: '14px' }}>In Progress</MenuItem>
              <MenuItem value="done" sx={{ fontSize: '14px' }}>Done</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.ticketType}
              onChange={(e) => setFilters({...filters, ticketType: e.target.value})}
              variant="outlined"
              displayEmpty
              sx={{
                height: 40,
                fontSize: '14px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '14px' }}>Type: All</MenuItem>
              <MenuItem value="task" sx={{ fontSize: '14px' }}>Task</MenuItem>
              <MenuItem value="bug" sx={{ fontSize: '14px' }}>Bug</MenuItem>
              <MenuItem value="story" sx={{ fontSize: '14px' }}>Story</MenuItem>
              <MenuItem value="epic" sx={{ fontSize: '14px' }}>Epic</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.epic}
              onChange={(e) => setFilters({...filters, epic: e.target.value})}
              variant="outlined"
              displayEmpty
              sx={{
                height: 40,
                fontSize: '14px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '14px' }}>Epic: All</MenuItem>
              <MenuItem value="Security Enhancement" sx={{ fontSize: '14px' }}>Security Enhancement</MenuItem>
              <MenuItem value="User Management" sx={{ fontSize: '14px' }}>User Management</MenuItem>
              <MenuItem value="UI/UX Improvements" sx={{ fontSize: '14px' }}>UI/UX Improvements</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.label}
              onChange={(e) => setFilters({...filters, label: e.target.value})}
              variant="outlined"
              displayEmpty
              sx={{
                height: 40,
                fontSize: '14px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                }
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '14px' }}>Label: All</MenuItem>
              <MenuItem value="frontend" sx={{ fontSize: '14px' }}>Frontend</MenuItem>
              <MenuItem value="backend" sx={{ fontSize: '14px' }}>Backend</MenuItem>
              <MenuItem value="security" sx={{ fontSize: '14px' }}>Security</MenuItem>
            </Select>
          </FormControl>

          {/* More Button */}
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            sx={{ 
              height: 40,
              px: 2,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderColor: 'primary.main',
              color: 'primary.main',
              borderRadius: 2,
              '&:hover': { 
                bgcolor: 'primary.50',
                borderColor: 'primary.main'
              }
            }}
          >
            {showMoreFilters ? 'Less' : 'More'}
          </Button>
        </Box>

        {/* Additional Filters (Priority, Team, Reporter) */}
        {showMoreFilters && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider'
          }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                variant="outlined"
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: '14px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                  }
                }}
              >
                <MenuItem value="All" sx={{ fontSize: '14px' }}>Priority: All</MenuItem>
                <MenuItem value="high" sx={{ fontSize: '14px' }}>High</MenuItem>
                <MenuItem value="medium" sx={{ fontSize: '14px' }}>Medium</MenuItem>
                <MenuItem value="low" sx={{ fontSize: '14px' }}>Low</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.team}
                onChange={(e) => setFilters({...filters, team: e.target.value})}
                variant="outlined"
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: '14px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                  }
                }}
              >
                <MenuItem value="All" sx={{ fontSize: '14px' }}>Team: All</MenuItem>
                <MenuItem value="frontend" sx={{ fontSize: '14px' }}>Frontend</MenuItem>
                <MenuItem value="backend" sx={{ fontSize: '14px' }}>Backend</MenuItem>
                <MenuItem value="devops" sx={{ fontSize: '14px' }}>DevOps</MenuItem>
                <MenuItem value="qa" sx={{ fontSize: '14px' }}>QA</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.reporter}
                onChange={(e) => setFilters({...filters, reporter: e.target.value})}
                variant="outlined"
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: '14px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                  }
                }}
              >
                <MenuItem value="All" sx={{ fontSize: '14px' }}>Reporter: All</MenuItem>
                <MenuItem value="S" sx={{ fontSize: '14px' }}>S</MenuItem>
                <MenuItem value="K" sx={{ fontSize: '14px' }}>K</MenuItem>
                <MenuItem value="A" sx={{ fontSize: '14px' }}>A</MenuItem>
                <MenuItem value="YT" sx={{ fontSize: '14px' }}>YT</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Search Results Counter */}
      {filters.searchTerm && (
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${filteredTickets.length} of ${tickets.length} tickets`}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ fontSize: '12px' }}
            />
          </Box>
        </Box>
      )}

      {/* Table */}
      <Box sx={{ p: 3 }}>
        {filteredTickets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {filters.searchTerm ? (
              <>
                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No tickets found for "{filters.searchTerm}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Try adjusting your search terms or filters
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setFilters({...filters, searchTerm: ''})}
                >
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No tickets found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your filters or create new tickets
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox">
                    <input type="checkbox" />
                  </TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>A</TableCell>
                  <TableCell>R</TableCell>
                  <TableCell align="right">⋮</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id} sx={{ p: 0 }}>
                    <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 1.5, 
                          m: 1,
                          borderRadius: 1.5, 
                          border: 1, 
                          borderColor: "divider", 
                          transition: "all 0.2s ease-in-out", 
                          "&:hover": { transform: "translateY(-1px)", boxShadow: 3, borderColor: `${getStatusColor(ticket.status)}.main` },
                          bgcolor: 'background.paper',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2, 
                          width: '100%', 
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          {/* Left side - Icon and ticket info */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5,
                            flex: 1,
                            minWidth: 0
                          }}>
                            <Avatar sx={{ 
                              bgcolor: `${getStatusColor(ticket.status)}.light`, 
                              color: `${getStatusColor(ticket.status)}.dark`, 
                              width: 32, 
                              height: 32,
                              boxShadow: 1
                            }}>
                              {getTicketIcon(ticket.type || 'task')}
                            </Avatar>
                            
                            <Box sx={{ 
                              flex: 1, 
                              minWidth: 0, 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  color="text.primary" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  #{ticket.id}
                                </Typography>
                                <Chip 
                                  label={ticket.status?.toUpperCase() || 'UNKNOWN'} 
                                  color={getStatusColor(ticket.status)} 
                                  size="small" 
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                              
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  maxWidth: 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {filters.searchTerm && ticket.title ? (
                                  <span>
                                    {ticket.title.split(new RegExp(`(${filters.searchTerm})`, 'gi')).map((part, i) => 
                                      part.toLowerCase() === filters.searchTerm.toLowerCase() ? (
                                        <span key={i} style={{ backgroundColor: 'rgba(25, 118, 210, 0.2)', fontWeight: 600 }}>
                                          {part}
                                        </span>
                                      ) : part
                                    )}
                                  </span>
                                ) : (
                                  ticket.title || 'No title'
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Right side - Assignees and Actions */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            flexShrink: 0
                          }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                Assignee
                              </Typography>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                {parseJsonField(ticket.assignees)[0] || '?'}
                              </Avatar>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                Reporter
                              </Typography>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                {parseJsonField(ticket.assignees)[1] || '-'}
                              </Avatar>
                            </Box>
                            
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <MoreVertIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Dialog>
  );
}

// Status trend chart component
function TrendByStatusChart({ title, data, height = 300 }) {
  const [chartType, setChartType] = useState("daily");
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
  
  // Add comprehensive safety check for empty data
  if (!data || !data[chartType] || !Array.isArray(data[chartType]) || data[chartType].length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      </Paper>
    );
  }
  
  const chartData = data[chartType];
  
  // Additional validation to ensure chartData has the required properties
  if (!chartData.every(d => d && typeof d === 'object')) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Invalid data format</Typography>
        </Box>
      </Paper>
    );
  }
  
  const dailyChartData = {
    labels: chartData.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'To Do',
        data: chartData.map(d => d.todo),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'In Progress',
        data: chartData.map(d => d.inProgress),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Done',
        data: chartData.map(d => d.done),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Blocked',
        data: chartData.map(d => d.blocked),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  const weeklyChartData = {
    labels: chartData.map(w => w.week),
    datasets: [
      {
        label: 'To Do',
        data: chartData.map(w => w.todo),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'In Progress',
        data: chartData.map(w => w.inProgress),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Done',
        data: chartData.map(w => w.done),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Blocked',
        data: chartData.map(w => w.blocked),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length === 0) return [];
            
            return data.datasets.map((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              if (!meta.visible) return null;
              
              return {
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 2,
                hidden: !meta.visible,
                index: i
              };
            }).filter(label => label !== null);
          },
          onClick: function(e, legendItem, legend) {
            try {
              const index = legendItem.index;
              const ci = legend.chart;
              
              // Prevent the default Chart.js legend click behavior
              e.preventDefault();
              e.stopPropagation();
              
              if (ci && ci.data && ci.data.datasets && ci.data.datasets[index]) {
                const meta = ci.getDatasetMeta(index);
                if (meta) {
                  // Toggle visibility manually
                  if (meta.visible) {
                    meta.visible = false;
                    legendItem.hidden = true;
                    setHiddenDatasets(prev => new Set([...prev, index]));
                  } else {
                    meta.visible = true;
                    legendItem.hidden = false;
                    setHiddenDatasets(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(index);
                      return newSet;
                    });
                  }
                  
                  // Update the chart safely
                  try {
                    ci.update('none');
                  } catch (updateError) {
                    console.warn('Chart update error:', updateError);
                  }
                }
              }
            } catch (error) {
              console.warn('Legend click error:', error);
            }
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        position: 'nearest',
        xAlign: 'auto',
        yAlign: 'auto',
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const dataset = context.dataset;
            const value = context.parsed.y;
            const label = dataset.label;
            
            // Show actual data values with proper formatting
            if (chartType === "daily") {
              const date = new Date(chartData[context.dataIndex].date);
              return `${label}: ${value}`;
            } else {
              return `${label}: ${value}`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Stack direction="row" spacing={1}>
          <Button 
            size="small" 
            variant={chartType === "daily" ? "contained" : "outlined"}
            onClick={() => setChartType("daily")}
          >
            Daily
          </Button>
          <Button 
            size="small" 
            variant={chartType === "weekly" ? "contained" : "outlined"}
            onClick={() => setChartType("weekly")}
          >
            Weekly
          </Button>
        </Stack>
      </Stack>
      
      <Box sx={{ height: height, position: "relative", width: '100%', '& canvas': { width: '100% !important' } }}>
        {(() => {
          try {
            return chartType === "daily" ? (
              <Line data={dailyChartData} options={chartOptions} />
            ) : (
              <Line data={weeklyChartData} options={chartOptions} />
            );
          } catch (error) {
            console.error('Chart rendering error:', error);
            return (
              <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="error.main">Chart rendering error</Typography>
              </Box>
            );
          }
        })()}
      </Box>
    </Paper>
  );
}

// Backlog chart component with same design as TrendByStatusChart
function BacklogChart({ title, data, height = 300 }) {
  const [chartType, setChartType] = useState("daily");
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
  
  // Add comprehensive safety check for empty data
  if (!data || !data[chartType] || !Array.isArray(data[chartType]) || data[chartType].length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      </Paper>
    );
  }
  
  const chartData = data[chartType];
  
  // Additional validation to ensure chartData has the required properties
  if (!chartData.every(d => d && typeof d === 'object')) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Invalid data format</Typography>
        </Box>
      </Paper>
    );
  }
  
  const dailyChartData = {
    labels: chartData.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Task',
        data: chartData.map(d => d.todo || 0),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Bug',
        data: chartData.map(d => d.inProgress || 0),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Story',
        data: chartData.map(d => d.done || 0),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      
      {
        label: 'Subtask',
        data: chartData.map(d => (d.todo || 0) + (d.inProgress || 0)),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  const weeklyChartData = {
    labels: chartData.map(w => w.week),
    datasets: [
      {
        label: 'Task',
        data: chartData.map(w => w.todo || 0),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Bug',
        data: chartData.map(w => w.inProgress || 0),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Story',
        data: chartData.map(w => w.done || 0),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      
      {
        label: 'Subtask',
        data: chartData.map(w => (w.todo || 0) + (w.inProgress || 0)),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length === 0) return [];
            
            return data.datasets.map((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              if (!meta.visible) return null;
              
              return {
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 2,
                hidden: !meta.visible,
                index: i
              };
            }).filter(label => label !== null);
          },
          onClick: function(e, legendItem, legend) {
            try {
              const index = legendItem.index;
              const ci = legend.chart;
              
              // Prevent the default Chart.js legend click behavior
              e.preventDefault();
              e.stopPropagation();
              
              if (ci && ci.data && ci.data.datasets && ci.data.datasets[index]) {
                const meta = ci.getDatasetMeta(index);
                if (meta) {
                  // Toggle visibility manually
                  if (meta.visible) {
                    meta.visible = false;
                    legendItem.hidden = true;
                    setHiddenDatasets(prev => new Set([...prev, index]));
                  } else {
                    meta.visible = true;
                    legendItem.hidden = false;
                    setHiddenDatasets(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(index);
                      return newSet;
                    });
                  }
                  
                  // Update the chart safely
                  try {
                    ci.update('none');
                  } catch (updateError) {
                    console.warn('Chart update error:', updateError);
                  }
                }
              }
            } catch (error) {
              console.warn('Legend click error:', error);
            }
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        position: 'nearest',
        xAlign: 'auto',
        yAlign: 'auto',
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const dataset = context.dataset;
            const value = context.parsed.y;
            const label = dataset.label;
            
            // Show actual data values with proper formatting
            if (chartType === "daily") {
              const date = new Date(chartData[context.dataIndex].date);
              return `${label}: ${value}`;
            } else {
              return `${label}: ${value}`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider", width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Stack direction="row" spacing={1}>
          <Button 
            size="small" 
            variant={chartType === "daily" ? "contained" : "outlined"}
            onClick={() => setChartType("daily")}
          >
            Daily
          </Button>
          <Button 
            size="small" 
            variant={chartType === "weekly" ? "contained" : "outlined"}
            onClick={() => setChartType("weekly")}
          >
            Weekly
          </Button>
        </Stack>
      </Stack>
      
      <Box sx={{ height: height, position: "relative", width: '100%', '& canvas': { width: '100% !important' } }}>
        {(() => {
          try {
            return chartType === "daily" ? (
              <Line data={dailyChartData} options={chartOptions} />
            ) : (
              <Line data={weeklyChartData} options={chartOptions} />
            );
          } catch (error) {
            console.error('Chart rendering error:', error);
            return (
              <Box sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="error.main">Chart rendering error</Typography>
              </Box>
            );
          }
        })()}
      </Box>
    </Paper>
  );
}

function MemberWorkloadTable({ members }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("all"); // all, name, role, status
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState(members);
  
  // Add safety check for empty members array
  if (!members || members.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider" }}>
        <Typography variant="h6" fontWeight={600}>Member's Workload</Typography>
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No team members found</Typography>
        </Box>
      </Paper>
    );
  }

  // Enhanced search function with multiple criteria
  const performSearch = (term, criteria) => {
    if (!term.trim()) {
      setFilteredMembers(members);
      return;
    }

    const searchLower = term.toLowerCase();
    const filtered = members.filter(member => {
      switch (criteria) {
        case 'name':
          return member.name.toLowerCase().includes(searchLower);
        case 'role':
          return member.role?.toLowerCase().includes(searchLower);
        case 'status':
          return (
            (member.todo > 0 && 'todo'.includes(searchLower)) ||
            (member.inProgress > 0 && 'in progress'.includes(searchLower)) ||
            (member.done > 0 && 'done'.includes(searchLower))
          );
        case 'occupancy':
          return member.occupancy?.includes(searchLower);
        case 'all':
        default:
          return (
            member.name.toLowerCase().includes(searchLower) ||
            member.role?.toLowerCase().includes(searchLower) ||
            member.occupancy?.includes(searchLower) ||
            member.totalAssigned.toString().includes(searchLower) ||
            member.todo.toString().includes(searchLower) ||
            member.inProgress.toString().includes(searchLower) ||
            member.done.toString().includes(searchLower)
          );
      }
    });
    
    setFilteredMembers(filtered);
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    performSearch(value, searchCriteria);
    setShowSuggestions(value.length > 0);
  };

  // Handle search criteria change
  const handleCriteriaChange = (event) => {
    const newCriteria = event.target.value;
    setSearchCriteria(newCriteria);
    performSearch(searchTerm, newCriteria);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSearchCriteria("all");
    setFilteredMembers(members);
    setShowSuggestions(false);
  };

  // Get search suggestions
  const getSearchSuggestions = () => {
    if (!searchTerm.trim()) return [];
    
    const suggestions = [];
    const searchLower = searchTerm.toLowerCase();
    
    // Add name suggestions
    members.forEach(member => {
      if (member.name.toLowerCase().includes(searchLower)) {
        suggestions.push({ type: 'name', value: member.name, member });
      }
    });
    
    // Add role suggestions
    const roles = [...new Set(members.map(m => m.role))];
    roles.forEach(role => {
      if (role?.toLowerCase().includes(searchLower)) {
        suggestions.push({ type: 'role', value: role });
      }
    });
    
    // Add status suggestions
    if (searchLower.includes('todo') || searchLower.includes('in progress') || searchLower.includes('done')) {
      suggestions.push({ type: 'status', value: 'High workload members' });
      suggestions.push({ type: 'status', value: 'Available members' });
    }
    
    return suggestions.slice(0, 5); // Limit to 5 suggestions
  };

  const suggestions = getSearchSuggestions();
  
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Member's Workload</Typography>
        
        {/* Enhanced Search Bar */}
        <Box sx={{ position: 'relative', display: "flex", alignItems: "center", gap: 1 }}>
          {/* Search Criteria Selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={searchCriteria}
              onChange={handleCriteriaChange}
              variant="outlined"
              size="small"
              sx={{ 
                height: 40,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
              }}
            >
              <MenuItem value="all">All Fields</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="role">Role</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="occupancy">Occupancy</MenuItem>
            </Select>
          </FormControl>
          
          {/* Enhanced Search Input */}
          <Box sx={{ 
            position: 'relative',
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            border: 1, 
            borderColor: showSuggestions ? "primary.main" : "divider", 
            borderRadius: 2, 
            px: 1.25,
            py: 0.5,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
            },
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
            }
          }}>
            <SearchIcon 
              fontSize="small" 
              color={showSuggestions ? "primary" : "action"} 
              sx={{ 
                position: 'absolute', 
                left: 12, 
                zIndex: 1 
              }}
            />
            <TextField 
              size="small" 
              variant="standard" 
              placeholder={`Search ${searchCriteria === 'all' ? 'all fields' : searchCriteria}...`}
              InputProps={{ 
                disableUnderline: true,
                sx: { 
                  minWidth: 200,
                  textAlign: 'left',
                  pl: 4, // Add left padding to account for the icon
                  pr: 4, // Add right padding for balance
                  '& input': {
                    textAlign: 'left',
                    '&::placeholder': {
                      textAlign: 'left',
                      opacity: 1
                    }
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  textAlign: 'left'
                }
              }}
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(searchTerm.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            />
            
            {/* Clear Button */}
            {searchTerm && (
              <IconButton
                size="small"
                onClick={clearSearch}
                sx={{ 
                  position: 'absolute',
                  right: 8,
                  p: 0.5, 
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                  zIndex: 1
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            
            {/* Search Suggestions Dropdown - Positioned relative to search input */}
            {showSuggestions && suggestions.length > 0 && (
              <Paper 
                elevation={3} 
                sx={{ 
                  position: 'absolute', 
                  top: 'calc(100% + 8px)', 
                  left: 0, 
                  right: 0,
                  zIndex: 1000, 
                  minWidth: 300,
                  maxHeight: 200,
                  overflow: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <Stack spacing={0}>
                  {suggestions.map((suggestion, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderBottom: index < suggestions.length - 1 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                      onClick={() => {
                        setSearchTerm(suggestion.value);
                        performSearch(suggestion.value, searchCriteria);
                        setShowSuggestions(false);
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: suggestion.type === 'name' ? 'primary.main' : 
                                   suggestion.type === 'role' ? 'success.main' : 'warning.main' 
                        }} />
                        <Typography variant="body2" fontWeight={500}>
                          {suggestion.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {suggestion.type}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}
          </Box>
          
          {/* Search Results Count */}
          {searchTerm && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {filteredMembers.length} of {members.length}
            </Typography>
          )}
        </Box>
      </Stack>
      
      {/* Remove the old suggestions dropdown that was incorrectly positioned */}
       
       {/* Enhanced Table Container */}
       <TableContainer sx={{
         maxHeight: 400,
         overflow: 'auto',
         scrollBehavior: 'smooth',
         '&::-webkit-scrollbar': {
           width: '8px',
           height: '8px',
         },
         '&::-webkit-scrollbar-track': {
           background: 'transparent',
         },
         '&::-webkit-scrollbar-thumb': {
           background: 'rgba(156, 163, 175, 0.3)',
           borderRadius: '4px',
           '&:hover': {
             background: 'rgba(156, 163, 175, 0.6)',
           }
         },
         // Improve table responsiveness on laptop screens
         '& .MuiTable-root': {
           minWidth: { xs: 600, sm: 800, md: 1000 }
         },
         // Ensure proper table display on smaller screens
         '& .MuiTableCell-root': {
           padding: { xs: '8px 4px', sm: '8px 6px', md: '16px' },
           fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
         }
       }}>
         <Table size="small">
           <TableHead>
             <TableRow sx={{ bgcolor: "grey.50" }}>
               <TableCell>Members</TableCell>
               <TableCell align="center">Total Assigned</TableCell>
               <TableCell align="center">Todo</TableCell>
               <TableCell align="center">In Progress</TableCell>
               <TableCell align="center">Done</TableCell>
               <TableCell align="center">Time Spent</TableCell>
               <TableCell align="center">Occupancy</TableCell>
             </TableRow>
           </TableHead>
           <TableBody>
             {filteredMembers.length === 0 ? (
               <TableRow>
                 <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                   <Stack spacing={1} alignItems="center">
                     <SearchIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                     <Typography variant="body2" color="text.secondary">
                       No members found matching "{searchTerm}"
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       Try adjusting your search criteria
                     </Typography>
                   </Stack>
                 </TableCell>
               </TableRow>
             ) : (
               filteredMembers.map((member) => (
                 <TableRow key={member.id} hover>
                   <TableCell>
                     <Stack direction="row" spacing={1} alignItems="center">
                       <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main", fontSize: 12 }}>
                         {member.avatar}
                       </Avatar>
                       <Typography variant="body2">{member.name}</Typography>
                     </Stack>
                   </TableCell>
                   <TableCell align="center">
                     <Typography variant="body2" fontWeight={600}>{member.totalAssigned}</Typography>
                   </TableCell>
                   <TableCell align="center">
                     <Chip label={member.todo} color="warning" size="small" />
                   </TableCell>
                   <TableCell align="center">
                     <Chip label={member.inProgress} color="info" size="small" />
                   </TableCell>
                   <TableCell align="center">
                     <Chip label={member.done} color="success" size="small" />
                   </TableCell>
                   <TableCell align="center">
                     <Typography variant="body2" color="text.secondary">{member.timeSpent}</Typography>
                   </TableCell>
                   <TableCell align="center">
                     <Typography variant="body2" color="text.secondary">{member.occupancy}</Typography>
                   </TableCell>
                 </TableRow>
               ))
             )}
           </TableBody>
         </Table>
       </TableContainer>
     </Paper>
   );
 }

function SprintMetrics() {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider" }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Current Sprint</Typography>
          <Typography variant="h4" color="primary.main" gutterBottom>No Active Sprint</Typography>
          
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Progress</Typography>
                <Typography variant="body2" fontWeight={600}>
                  0%
                </Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={0} 
                sx={{ height: 8, borderRadius: 999 }}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Velocity</Typography>
                <Typography variant="h6" fontWeight={600}>0</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Remaining</Typography>
                <Typography variant="h6" fontWeight={600}>0</Typography>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: 1, borderColor: "divider" }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Previous Sprint</Typography>
          <Typography variant="h4" color="success.main" gutterBottom>No Previous Sprint</Typography>
          
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
                <Typography variant="body2" fontWeight={600}>
                  0%
                </Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={0} 
                sx={{ height: 8, borderRadius: 999 }}
                color="success"
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Velocity</Typography>
                <Typography variant="h6" fontWeight={600}>0</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
                <Typography variant="h6" fontWeight={600}>0</Typography>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}

function PerformanceMetrics() {
  const avgVelocity = 0;
  const totalCompleted = 0;
  const totalCreated = 0;
  const completionRate = 0;
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", textAlign: "center" }}>
          <Typography variant="h6" color="info.main" fontWeight={600}>{avgVelocity.toFixed(1)}</Typography>
          <Typography variant="body2" color="text.secondary">Avg Velocity</Typography>
          <Typography variant="caption" color="text.disabled">Story points/week</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", textAlign: "center" }}>
          <Typography variant="h6" color="success.main" fontWeight={600}>{totalCompleted}</Typography>
          <Typography variant="body2" color="text.secondary">Total Completed</Typography>
          <Typography variant="caption" color="text.disabled">Last 12 weeks</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", textAlign: "center" }}>
          <Typography variant="h6" color="warning.main" fontWeight={600}>{totalCreated}</Typography>
          <Typography variant="body2" color="text.secondary">Total Created</Typography>
          <Typography variant="caption" color="text.disabled">Last 12 weeks</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", textAlign: "center" }}>
          <Typography variant="h6" color="primary.main" fontWeight={600}>{completionRate.toFixed(1)}%</Typography>
          <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
          <Typography variant="caption" color="text.disabled">Success ratio</Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

function ExportActions() {
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState('');

  // Get data from context or props (you'll need to pass this data)
  const getExportData = () => {
    // Mock data - replace with actual data from your app state
    return {
      tickets: [
        { id: 1, title: 'Implement User Authentication', status: 'In Progress', type: 'Feature', priority: 'High', assignee: 'John Doe', created: '2024-01-15' },
        { id: 2, title: 'Fix Login Bug', status: 'Done', type: 'Bug', priority: 'Medium', assignee: 'Jane Smith', created: '2024-01-10' },
        { id: 3, title: 'Update Documentation', status: 'Todo', type: 'Task', priority: 'Low', assignee: 'Mike Johnson', created: '2024-01-20' }
      ],
      sprints: [
        { id: 1, name: 'Sprint 1', startDate: '2024-01-01', endDate: '2024-01-15', status: 'Completed', velocity: 85 },
        { id: 2, name: 'Sprint 2', startDate: '2024-01-16', endDate: '2024-01-31', status: 'Active', velocity: 72 }
      ],
      members: [
        { name: 'John Doe', role: 'Developer', tasksCompleted: 15, currentTasks: 3 },
        { name: 'Jane Smith', role: 'QA Engineer', tasksCompleted: 12, currentTasks: 2 },
        { name: 'Mike Johnson', role: 'Designer', tasksCompleted: 8, currentTasks: 1 }
      ]
    };
  };

  const exportToPDF = async () => {
    setExporting(true);
    setExportType('PDF');
    
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = await import('jspdf-autotable');
      
      const doc = new jsPDF();
      const data = getExportData();
      
      // Company name and date header
      const companyName = 'Pagatur Software Pvt. Ltd.';
      const currentDate = new Date().toLocaleDateString();
      
      doc.setFontSize(16);
      doc.text(companyName, 20, 15);
      doc.setFontSize(10);
      doc.text(`Date: ${currentDate}`, 20, 25);
      
      // Title
      doc.setFontSize(20);
      doc.text('Task Management Report', 105, 40, { align: 'center' });
      
      // Summary
      doc.setFontSize(12);
      doc.text(`Total Tickets: ${data.tickets.length}`, 20, 70);
      doc.text(`Total Sprints: ${data.sprints.length}`, 20, 80);
      doc.text(`Team Members: ${data.members.length}`, 20, 90);
      
      // Tickets Table
      doc.setFontSize(14);
      doc.text('Tickets Overview', 20, 110);
      
      const ticketsData = data.tickets.map(t => [
        t.id, t.title, t.status, t.type, t.priority, t.assignee, t.created
      ]);
      
      autoTable.default(doc, {
        head: [['ID', 'Title', 'Status', 'Type', 'Priority', 'Assignee', 'Created']],
        body: ticketsData,
        startY: 120,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 139, 202] }
      });
      
      // Sprints Table
      const sprintsY = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(14);
      doc.text('Sprints Overview', 20, sprintsY);
      
      const sprintsData = data.sprints.map(s => [
        s.id, s.name, s.startDate, s.endDate, s.status, s.velocity
      ]);
      
      autoTable.default(doc, {
        head: [['ID', 'Name', 'Start Date', 'End Date', 'Status', 'Velocity']],
        body: sprintsData,
        startY: sprintsY + 10,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [92, 184, 92] }
      });
      
      // Save the PDF
      doc.save('task-management-report.pdf');
      
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    setExportType('CSV');
    
    try {
      const { saveAs } = await import('file-saver');
      const data = getExportData();
      
      // Create CSV content
      let csvContent = 'ID,Title,Status,Type,Priority,Assignee,Created\n';
      
      data.tickets.forEach(ticket => {
        csvContent += `${ticket.id},"${ticket.title}",${ticket.status},${ticket.type},${ticket.priority},"${ticket.assignee}",${ticket.created}\n`;
      });
      
      // Add sprints data
      csvContent += '\nSprint ID,Name,Start Date,End Date,Status,Velocity\n';
      data.sprints.forEach(sprint => {
        csvContent += `${sprint.id},"${sprint.name}",${sprint.startDate},${sprint.endDate},${sprint.status},${sprint.velocity}\n`;
      });
      
      // Add team members data
      csvContent += '\nName,Role,Tasks Completed,Current Tasks\n';
      data.members.forEach(member => {
        csvContent += `"${member.name}",${member.role},${member.tasksCompleted},${member.currentTasks}\n`;
      });
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'task-management-data.csv');
      
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Error generating CSV. Please try again.');
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const generateReport = async () => {
    setExporting(true);
    setExportType('Report');
    
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = await import('jspdf-autotable');
      const { saveAs } = await import('file-saver');
      
      const data = getExportData();
      
      // Create comprehensive report
      const doc = new jsPDF();
      
      // Company name and date header
      const companyName = 'Pagatur Software Pvt. Ltd.';
      const currentDate = new Date().toLocaleDateString();
      
      doc.setFontSize(16);
      doc.text(companyName, 20, 15);
      doc.setFontSize(10);
      doc.text(`Date: ${currentDate}`, 20, 25);
      
      // Cover page
      doc.setFontSize(24);
      doc.text('Task Management', 105, 50, { align: 'center' });
      doc.setFontSize(16);
      doc.text('Comprehensive Report', 105, 65, { align: 'center' });
      doc.setFontSize(12);
      doc.text(`Generated: ${currentDate}`, 105, 80, { align: 'center' });
      
      // Add new page for detailed analysis
      doc.addPage();
      
      // Executive Summary
      doc.setFontSize(18);
      doc.text('Executive Summary', 20, 20);
      doc.setFontSize(12);
      
      const totalTickets = data.tickets.length;
      const completedTickets = data.tickets.filter(t => t.status === 'Done').length;
      const inProgressTickets = data.tickets.filter(t => t.status === 'In Progress').length;
      const completionRate = totalTickets > 0 ? ((completedTickets / totalTickets) * 100).toFixed(1) : 0;
      
      doc.text(`• Total Active Tickets: ${totalTickets}`, 20, 40);
      doc.text(`• Completed Tickets: ${completedTickets}`, 20, 50);
      doc.text(`• In Progress: ${inProgressTickets}`, 20, 60);
      doc.text(`• Completion Rate: ${completionRate}%`, 20, 70);
      
      // Performance Metrics
      doc.setFontSize(16);
      doc.text('Performance Metrics', 20, 100);
      doc.setFontSize(12);
      
      const avgVelocity = data.sprints.reduce((sum, s) => sum + s.velocity, 0) / data.sprints.length;
      doc.text(`• Average Sprint Velocity: ${avgVelocity.toFixed(1)}`, 20, 120);
      doc.text(`• Active Sprints: ${data.sprints.filter(s => s.status === 'Active').length}`, 20, 130);
      doc.text(`• Team Members: ${data.members.length}`, 20, 140);
      
      // Detailed Tables
      const tablesY = 170;
      doc.setFontSize(16);
      doc.text('Detailed Analysis', 20, tablesY);
      
      // Tickets by Status
      const statusCounts = {};
      data.tickets.forEach(t => {
        statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
      });
      
      const statusData = Object.entries(statusCounts).map(([status, count]) => [status, count]);
      autoTable.default(doc, {
        head: [['Status', 'Count']],
        body: statusData,
        startY: tablesY + 20,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 139, 202] }
      });
      
      // Team Performance
      const teamY = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(16);
      doc.text('Team Performance', 20, teamY);
      
      const teamData = data.members.map(m => [m.name, m.role, m.tasksCompleted, m.currentTasks]);
      autoTable.default(doc, {
        head: [['Name', 'Role', 'Tasks Completed', 'Current Tasks']],
        body: teamData,
        startY: teamY + 20,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [92, 184, 92] }
      });
      
      // Save the comprehensive report
      doc.save('comprehensive-task-report.pdf');
      
      // Also generate a summary CSV
      const summaryCsv = `Metric,Value\nTotal Tickets,${totalTickets}\nCompleted Tickets,${completedTickets}\nCompletion Rate,${completionRate}%\nAverage Velocity,${avgVelocity.toFixed(1)}\nTeam Size,${data.members.length}`;
      const blob = new Blob([summaryCsv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'task-summary.csv');
      
    } catch (error) {
      console.error('Report generation error:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const handleExport = async (format) => {
    switch (format) {
      case 'PDF':
        await exportToPDF();
        break;
      case 'CSV':
        await exportToCSV();
        break;
      case 'Report':
        await generateReport();
        break;
      default:
        // Unknown export format
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight={600}>Export & Reports</Typography>
        <Stack direction="row" spacing={1}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => handleExport('PDF')}
            startIcon={<BoltIcon />}
            disabled={exporting}
          >
            {exporting && exportType === 'PDF' ? 'Generating...' : 'Export PDF'}
          </Button>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => handleExport('CSV')}
            startIcon={<BoltIcon />}
            disabled={exporting}
          >
            {exporting && exportType === 'CSV' ? 'Generating...' : 'Export CSV'}
          </Button>
          <Button 
            size="small" 
            variant="contained" 
            onClick={() => handleExport('Report')}
            startIcon={<BoltIcon />}
            disabled={exporting}
          >
            {exporting && exportType === 'Report' ? 'Generating...' : 'Generate Report'}
          </Button>
        </Stack>
      </Stack>
      
      {exporting && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Generating {exportType.toLowerCase()}... Please wait.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ---------------- Active Sprints Modal ----------------
function ActiveSprintsModal({ open, onClose, sprints }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [progressMinFilter, setProgressMinFilter] = useState('');
  const [progressMaxFilter, setProgressMaxFilter] = useState('');
  const [ticketsMinFilter, setTicketsMinFilter] = useState('');
  const [ticketsMaxFilter, setTicketsMaxFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Enhanced filtering logic
  const filteredSprints = sprints.filter(sprint => {
    // Search filter
    const matchesSearch = !searchTerm || 
      sprint.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter with enhanced logic
    let matchesStatus = true;
    if (statusFilter === 'ALL') {
      matchesStatus = true;
    } else if (statusFilter === 'ACTIVE_FUTURE') {
      matchesStatus = sprint.status === 'ACTIVE' || sprint.status === 'FUTURE';
    } else {
      matchesStatus = sprint.status === statusFilter;
    }
    
    // Date range filters
    const matchesStartDate = !startDateFilter || sprint.start >= startDateFilter;
    const matchesEndDate = !endDateFilter || sprint.end <= endDateFilter;
    
    // Progress range filters
    const progressValue = sprint.progress * 100;
    const matchesProgressMin = !progressMinFilter || progressValue >= parseFloat(progressMinFilter);
    const matchesProgressMax = !progressMaxFilter || progressValue <= parseFloat(progressMaxFilter);
    
    // Tickets range filters
    const matchesTicketsMin = !ticketsMinFilter || sprint.tickets >= parseInt(ticketsMinFilter);
    const matchesTicketsMax = !ticketsMaxFilter || sprint.tickets <= parseInt(ticketsMaxFilter);
    
    return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate && 
           matchesProgressMin && matchesProgressMax && matchesTicketsMin && matchesTicketsMax;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSprints.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSprints = filteredSprints.slice(startIndex, startIndex + itemsPerPage);

  const getStatusChip = (status) => {
    const statusConfig = {
      'ACTIVE': { color: 'success', label: 'ACTIVE' },
      'FUTURE': { color: 'info', label: 'FUTURE' },
      'COMPLETED': { color: 'default', label: 'COMPLETED' }
    };
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getProgressValue = (sprint) => {
    if (sprint.status === 'COMPLETED') return 100;
    return Math.min(sprint.progress * 100, 100); // Ensure max 100%
  };

  const getProgressColor = (sprint) => {
    if (sprint.status === 'COMPLETED') return 'grey';
    if (sprint.progress >= 0.8) return 'success';
    if (sprint.progress >= 0.5) return 'warning';
    if (sprint.progress >= 0.2) return 'info';
    return 'error';
  };

  const getProgressLabel = (sprint) => {
    if (sprint.status === 'COMPLETED') return 'Completed';
    if (sprint.progress >= 0.8) return 'On Track';
    if (sprint.progress >= 0.5) return 'In Progress';
    if (sprint.progress >= 0.2) return 'Getting Started';
    return 'Not Started';
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('ACTIVE');
    setStartDateFilter('');
    setEndDateFilter('');
    setProgressMinFilter('');
    setProgressMaxFilter('');
    setTicketsMinFilter('');
    setTicketsMaxFilter('');
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || statusFilter !== 'ACTIVE' || startDateFilter || endDateFilter || 
                          progressMinFilter || progressMaxFilter || ticketsMinFilter || ticketsMaxFilter;

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Simulate loading state for demonstration
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      // Simulate API call delay
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Loading skeleton rows
  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} height={20} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} height={20} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={60} height={20} />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="rectangular" width={100} height={8} sx={{ borderRadius: 4 }} />
            <Skeleton variant="text" width={40} height={20} />
          </Box>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} height={20} />
        </TableCell>
        <TableCell>
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      </TableRow>
    ));
  };

  // Empty state component
  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" color="text.secondary">
            No sprints found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
            {searchTerm || hasActiveFilters 
              ? 'Try adjusting your search terms or filters to find more sprints.'
              : 'Get started by creating your first sprint to organize your work and track progress.'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewSprintModalOpen(true)}
            sx={{ mt: 1 }}
          >
            Create Sprint
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );

  // New Sprint Modal State
  const [newSprintModalOpen, setNewSprintModalOpen] = useState(false);
  const [newSprintData, setNewSprintData] = useState({
    name: '',
    start: new Date().toISOString().split('T')[0], // Default to today
    end: '',
    goal: ''
  });
  const [newSprintErrors, setNewSprintErrors] = useState({});

  // Validate new sprint data
  const validateNewSprint = () => {
    const errors = {};
    
    if (!newSprintData.name.trim()) {
      errors.name = 'Sprint name is required';
    }
    
    if (!newSprintData.start) {
      errors.start = 'Start date is required';
    }
    
    if (!newSprintData.end) {
      errors.end = 'End date is required';
    } else if (newSprintData.start && newSprintData.end < newSprintData.start) {
      errors.end = 'End date must be after or equal to start date';
    }
    
    setNewSprintErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle new sprint creation
  const handleCreateSprint = () => {
    if (validateNewSprint()) {
      const today = new Date().toISOString().split('T')[0];
      const status = newSprintData.start <= today ? 'ACTIVE' : 'FUTURE';
      
      const newSprint = {
        id: Date.now(), // Simple ID generation
        name: newSprintData.name.trim(),
        start: newSprintData.start,
        end: newSprintData.end,
        tickets: 0,
        progress: 0,
        active: status === 'ACTIVE',
        status: status,
        goal: newSprintData.goal.trim()
      };
      
      // Add to sprints array (in a real app, this would be an API call)
      sprints.push(newSprint);
      
      // Reset form and close modal
      setNewSprintData({
        name: '',
        start: new Date().toISOString().split('T')[0],
        end: '',
        goal: ''
      });
      setNewSprintErrors({});
      setNewSprintModalOpen(false);
      
      // Reset to page 1 to show the new sprint
      setCurrentPage(1);
    }
  };

  // Reset new sprint form
  const handleResetNewSprint = () => {
    setNewSprintData({
      name: '',
      start: new Date().toISOString().split('T')[0],
      end: '',
      goal: ''
    });
    setNewSprintErrors({});
  };

  // Edit Sprint Modal State
  const [editSprintModalOpen, setEditSprintModalOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [editSprintData, setEditSprintData] = useState({
    name: '',
    start: '',
    end: '',
    goal: ''
  });
  const [editSprintErrors, setEditSprintErrors] = useState({});

  // Open edit sprint modal
  const handleEditSprint = (sprint) => {
    setEditingSprint(sprint);
    setEditSprintData({
      name: sprint.name,
      start: sprint.start,
      end: sprint.end,
      goal: sprint.goal || ''
    });
    setEditSprintErrors({});
    setEditSprintModalOpen(true);
  };

  // Validate edit sprint data
  const validateEditSprint = () => {
    const errors = {};
    
    if (!editSprintData.name.trim()) {
      errors.name = 'Sprint name is required';
    }
    
    if (!editSprintData.start) {
      errors.start = 'Start date is required';
    }
    
    if (!editSprintData.end) {
      errors.end = 'End date is required';
    } else if (editSprintData.start && editSprintData.end < editSprintData.start) {
      errors.end = 'End date must be after or equal to start date';
    }
    
    setEditSprintErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle sprint update
  const handleUpdateSprint = () => {
    if (validateEditSprint()) {
      const today = new Date().toISOString().split('T')[0];
      const newStatus = editSprintData.start <= today ? 'ACTIVE' : 'FUTURE';
      
      // Update the sprint in the array
      const sprintIndex = sprints.findIndex(s => s.id === editingSprint.id);
      if (sprintIndex !== -1) {
        sprints[sprintIndex] = {
          ...sprints[sprintIndex],
          name: editSprintData.name.trim(),
          start: editSprintData.start,
          end: editSprintData.end,
          goal: editSprintData.goal.trim(),
          status: newStatus,
          active: newStatus === 'ACTIVE'
        };
      }
      
      // Close modal and reset
      setEditSprintModalOpen(false);
      setEditingSprint(null);
      setEditSprintData({});
      setEditSprintErrors({});
    }
  };

  // Reset edit sprint form
  const handleResetEditSprint = () => {
    if (editingSprint) {
      setEditSprintData({
        name: editingSprint.name,
        start: editingSprint.start,
        end: editingSprint.end,
        goal: editingSprint.goal || ''
      });
      setEditSprintErrors({});
    }
  };

                 // Complete Sprint Modal State
  const [completeSprintModalOpen, setCompleteSprintModalOpen] = useState(false);
  const [sprintToComplete, setSprintToComplete] = useState(null);

  // View Details Modal State
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [sprintToView, setSprintToView] = useState(null);

  // Delete Sprint Modal State
  const [deleteSprintModalOpen, setDeleteSprintModalOpen] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState(null);
         
             // Row Actions Menu State
             const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
             const [selectedSprint, setSelectedSprint] = useState(null);
         
             // Open complete sprint confirmation modal
             const handleCompleteSprint = (sprint) => {
               setSprintToComplete(sprint);
               setCompleteSprintModalOpen(true);
             };
         
             // Confirm and complete the sprint
             const handleConfirmCompleteSprint = () => {
               if (sprintToComplete) {
                 // Update the sprint status to COMPLETED
                 const sprintIndex = sprints.findIndex(s => s.id === sprintToComplete.id);
                 if (sprintIndex !== -1) {
                   sprints[sprintIndex] = {
                     ...sprints[sprintIndex],
                     status: 'COMPLETED',
                     active: false,
                     completedAt: new Date().toISOString()
                   };
                 }
         
                 // Close modal and reset
                 setCompleteSprintModalOpen(false);
                 setSprintToComplete(null);
               }
             };
         
               // Cancel complete sprint
  const handleCancelCompleteSprint = () => {
    setCompleteSprintModalOpen(false);
    setSprintToComplete(null);
  };

  // Open view details modal
  const handleViewSprintDetails = (sprint) => {
    setSprintToView(sprint);
    setViewDetailsModalOpen(true);
  };

  // Close view details modal
  const handleCloseViewDetails = () => {
    setViewDetailsModalOpen(false);
    setSprintToView(null);
  };

  // Open delete sprint confirmation
  const handleDeleteSprint = (sprint) => {
    setSprintToDelete(sprint);
    setDeleteSprintModalOpen(true);
  };

  // Cancel delete sprint
  const handleCancelDeleteSprint = () => {
    setDeleteSprintModalOpen(false);
    setSprintToDelete(null);
  };

  // Confirm delete sprint
  const handleConfirmDeleteSprint = () => {
    if (sprintToDelete) {
      // Remove sprint from the array
      const sprintIndex = sprints.findIndex(s => s.id === sprintToDelete.id);
      if (sprintIndex !== -1) {
        sprints.splice(sprintIndex, 1);
        // Force re-render by updating state
        setCurrentPage(1);
      }
      setDeleteSprintModalOpen(false);
      setSprintToDelete(null);
    }
  };
         
             // Open row actions menu
             const handleOpenActionsMenu = (event, sprint) => {
               setActionsMenuAnchor(event.currentTarget);
               setSelectedSprint(sprint);
             };
         
             // Close row actions menu
             const handleCloseActionsMenu = () => {
               setActionsMenuAnchor(null);
               setSelectedSprint(null);
             };
         
             // Handle row action selection
             const handleRowAction = (action) => {
               if (selectedSprint) {
                 switch (action) {
                   case 'edit':
                     handleEditSprint(selectedSprint);
                     break;
                   case 'complete':
                     handleCompleteSprint(selectedSprint);
                     break;
                   case 'delete':
                     handleDeleteSprint(selectedSprint);
                     break;
                   case 'view':
                     handleViewSprintDetails(selectedSprint);
                     break;
                   default:
                     break;
                 }
               }
               handleCloseActionsMenu();
             };

  // Simulate loading state for demonstration
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      // Simulate API call delay
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Active Sprints ({filteredSprints.length})
          </Typography>
          {hasActiveFilters && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {filteredSprints.length} of {sprints.length} sprints shown
            </Typography>
          )}
        </Box>
        <IconButton 
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
          aria-label="Close modal"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Search and Filters */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        {/* Primary Filters Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <TextField
              placeholder="Search sprints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element or perform search
                  const nextElement = e.target.parentElement.nextElementSibling?.querySelector('input, select, button');
                  if (nextElement) nextElement.focus();
                }
              }}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
              inputProps={{
                'aria-label': 'Search sprints by name',
                'role': 'searchbox'
              }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status Filter"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.nextElementSibling?.querySelector('input, select, button');
                  if (nextElement) nextElement.focus();
                }
              }}
              inputProps={{
                'aria-label': 'Filter sprints by status'
              }}
            >
              <MenuItem value="ALL">All Sprints</MenuItem>
              <MenuItem value="ACTIVE">Active Sprints</MenuItem>
              <MenuItem value="ACTIVE_FUTURE">Active & Future Sprints</MenuItem>
              <MenuItem value="FUTURE">Future Sprints</MenuItem>
              <MenuItem value="COMPLETED">Completed Sprints</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<BoltIcon />}
            size="small"
            onClick={() => setNewSprintModalOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setNewSprintModalOpen(true);
              }
            }}
            aria-label="Create new sprint"
          >
            New Sprint
          </Button>
        </Box>

        {/* Enhanced Filters Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          {/* Date Range Filters */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
              Start Date:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 150 }}
              inputProps={{
                'aria-label': 'Filter by start date'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
              End Date:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 150 }}
              inputProps={{
                'aria-label': 'Filter by end date'
              }}
            />
          </Box>

          {/* Progress Range Filters */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
              Progress:
            </Typography>
            <TextField
              placeholder="Min %"
              size="small"
              value={progressMinFilter}
              onChange={(e) => setProgressMinFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.nextElementSibling?.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 80 }}
              inputProps={{ 
                min: 0, 
                max: 100,
                'aria-label': 'Minimum progress percentage'
              }}
            />
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
            <TextField
              placeholder="Max %"
              size="small"
              value={progressMaxFilter}
              onChange={(e) => setProgressMaxFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 80 }}
              inputProps={{ 
                min: 0, 
                max: 100,
                'aria-label': 'Maximum progress percentage'
              }}
            />
          </Box>

          {/* Tickets Range Filters */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
              Tickets:
            </Typography>
            <TextField
              placeholder="Min"
              size="small"
              value={ticketsMinFilter}
              onChange={(e) => setTicketsMinFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.nextElementSibling?.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 80 }}
              inputProps={{ 
                min: 0,
                'aria-label': 'Minimum number of tickets'
              }}
            />
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
            <TextField
              placeholder="Max"
              size="small"
              value={ticketsMaxFilter}
              onChange={(e) => setTicketsMaxFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('button');
                  if (nextElement) nextElement.focus();
                }
              }}
              sx={{ width: 80 }}
              inputProps={{ 
                min: 0,
                'aria-label': 'Maximum number of tickets'
              }}
            />
          </Box>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={clearAllFilters}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  clearAllFilters();
                }
              }}
              sx={{ ml: 'auto' }}
              aria-label="Clear all active filters"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Sprints Table */}
      <Box sx={{ p: 3 }}>
        <Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sprint</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>No. of Tickets</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  renderSkeletonRows()
                ) : filteredSprints.length === 0 ? (
                  renderEmptyState()
                ) : (
                  paginatedSprints.map((sprint) => (
                    <TableRow key={sprint.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimelineIcon color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {sprint.name}
                            </Typography>
                            {getStatusChip(sprint.status)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{sprint.start}</TableCell>
                      <TableCell>{sprint.end}</TableCell>
                      <TableCell>{sprint.tickets}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            <LinearProgress
                              variant="determinate"
                              value={getProgressValue(sprint)}
                              color={getProgressColor(sprint)}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: sprint.status === 'COMPLETED' ? 'grey.300' : 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: sprint.status === 'COMPLETED' ? 'grey.500' : undefined,
                                  transition: 'transform 0.3s ease-in-out'
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'default'
                              }}
                              title={`${getProgressValue(sprint).toFixed(1)}% complete`}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  textShadow: '0 0 2px white',
                                  opacity: 0.8
                                }}
                              >
                                {getProgressValue(sprint).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '60px' }}>
                            <Typography 
                              variant="body2" 
                              color={sprint.status === 'COMPLETED' ? 'text.disabled' : 'text.secondary'}
                              sx={{ 
                                fontWeight: 500,
                                lineHeight: 1.2
                              }}
                            >
                              {Math.round(getProgressValue(sprint))}%
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color={sprint.status === 'COMPLETED' ? 'text.disabled' : 'text.secondary'}
                              sx={{ 
                                fontSize: '0.7rem',
                                opacity: 0.8,
                                lineHeight: 1.2
                              }}
                            >
                              {getProgressLabel(sprint)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {sprint.status === 'ACTIVE' && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              onClick={() => handleCompleteSprint(sprint)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleCompleteSprint(sprint);
                                }
                              }}
                              aria-label={`Complete sprint: ${sprint.name}`}
                            >
                              Complete Sprint
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEditSprint(sprint)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleEditSprint(sprint);
                              }
                            }}
                            aria-label={`Edit sprint: ${sprint.name}`}
                          >
                            Edit Sprint
                          </Button>
                          <IconButton 
                            size="small"
                            onClick={(event) => handleOpenActionsMenu(event, sprint)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleOpenActionsMenu(e, sprint);
                              }
                            }}
                            aria-label={`More actions for sprint: ${sprint.name}`}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredSprints.length)} of {filteredSprints.length}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  aria-label="Go to previous page"
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  aria-label="Go to next page"
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* New Sprint Modal */}
      <Dialog
        open={newSprintModalOpen}
        onClose={() => setNewSprintModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Create New Sprint
          </Typography>
          <IconButton onClick={() => setNewSprintModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sprint Name */}
            <TextField
              label="Sprint Name *"
              value={newSprintData.name}
              onChange={(e) => setNewSprintData({ ...newSprintData, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!newSprintErrors.name}
              helperText={newSprintErrors.name}
              fullWidth
              required
              inputProps={{
                'aria-label': 'Sprint name (required)'
              }}
            />

            {/* Start Date */}
            <TextField
              label="Start Date *"
              type="date"
              value={newSprintData.start}
              onChange={(e) => setNewSprintData({ ...newSprintData, start: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!newSprintErrors.start}
              helperText={newSprintErrors.start}
              fullWidth
              required
              inputProps={{
                min: new Date().toISOString().split('T')[0],
                'aria-label': 'Start date (required)'
              }}
            />

            {/* End Date */}
            <TextField
              label="End Date *"
              type="date"
              value={newSprintData.end}
              onChange={(e) => setNewSprintData({ ...newSprintData, end: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('textarea');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!newSprintErrors.end}
              helperText={newSprintErrors.end}
              fullWidth
              required
              inputProps={{
                min: newSprintData.start || new Date().toISOString().split('T')[0],
                'aria-label': 'End date (required)'
              }}
            />

            {/* Sprint Goal */}
            <TextField
              label="Sprint Goal (Optional)"
              value={newSprintData.goal}
              onChange={(e) => setNewSprintData({ ...newSprintData, goal: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  // Submit form on Enter (without Shift)
                  handleCreateSprint();
                }
              }}
              multiline
              rows={3}
              fullWidth
              placeholder="Describe the main objective of this sprint..."
              inputProps={{
                'aria-label': 'Sprint goal description (optional)'
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleResetNewSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleResetNewSprint();
                }
              }}
              aria-label="Reset sprint form"
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              onClick={() => setNewSprintModalOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setNewSprintModalOpen(false);
                }
              }}
              aria-label="Cancel sprint creation"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (newSprintData.name.trim() && newSprintData.start && newSprintData.end) {
                    handleCreateSprint();
                  }
                }
              }}
              disabled={!newSprintData.name.trim() || !newSprintData.start || !newSprintData.end}
              aria-label="Create new sprint"
            >
              Create Sprint
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Sprint Modal */}
      <Dialog
        open={editSprintModalOpen}
        onClose={() => setEditSprintModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Edit Sprint: {editingSprint?.name}
          </Typography>
          <IconButton onClick={() => setEditSprintModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sprint Name */}
            <TextField
              label="Sprint Name *"
              value={editSprintData.name || ''}
              onChange={(e) => setEditSprintData({ ...editSprintData, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!editSprintErrors.name}
              helperText={editSprintErrors.name}
              fullWidth
              required
              inputProps={{
                'aria-label': 'Sprint name (required)'
              }}
            />

            {/* Start Date */}
            <TextField
              label="Start Date *"
              type="date"
              value={editSprintData.start || ''}
              onChange={(e) => setEditSprintData({ ...editSprintData, start: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!editSprintErrors.start}
              helperText={editSprintErrors.start}
              fullWidth
              required
              inputProps={{
                'aria-label': 'Start date (required)'
              }}
            />

            {/* End Date */}
            <TextField
              label="End Date *"
              type="date"
              value={editSprintData.end || ''}
              onChange={(e) => setEditSprintData({ ...editSprintData, end: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next element
                  const nextElement = e.target.parentElement.parentElement.nextElementSibling?.querySelector('textarea');
                  if (nextElement) nextElement.focus();
                }
              }}
              error={!!editSprintErrors.end}
              helperText={editSprintErrors.end}
              fullWidth
              required
              inputProps={{
                min: editSprintData.start || '',
                'aria-label': 'End date (required)'
              }}
            />

            {/* Sprint Goal */}
            <TextField
              label="Sprint Goal (Optional)"
              value={editSprintData.goal || ''}
              onChange={(e) => setEditSprintData({ ...editSprintData, goal: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  // Submit form on Enter (without Shift)
                  handleUpdateSprint();
                }
              }}
              multiline
              rows={3}
              fullWidth
              placeholder="Describe the main objective of this sprint..."
              inputProps={{
                'aria-label': 'Sprint goal description (optional)'
              }}
            />

            {/* Status Info */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Changing dates may automatically update the sprint status:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Start date ≤ today → Status becomes ACTIVE
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Start date &gt; today → Status becomes FUTURE
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleResetEditSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleResetEditSprint();
                }
              }}
              aria-label="Reset sprint form"
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditSprintModalOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditSprintModalOpen(false);
                }
              }}
              aria-label="Cancel sprint editing"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (editSprintData.name?.trim() && editSprintData.start && editSprintData.end) {
                    handleUpdateSprint();
                  }
                }
              }}
              disabled={!editSprintData.name?.trim() || !editSprintData.start || !editSprintData.end}
              aria-label="Update sprint"
            >
              Update Sprint
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Complete Sprint Confirmation Modal */}
      <Dialog
        open={completeSprintModalOpen}
        onClose={handleCancelCompleteSprint}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Complete Sprint: {sprintToComplete?.name}
          </Typography>
          <IconButton onClick={handleCancelCompleteSprint}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to mark this sprint as completed? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCancelCompleteSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCancelCompleteSprint();
                }
              }}
              aria-label="Cancel sprint completion"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmCompleteSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleConfirmCompleteSprint();
                }
              }}
              aria-label="Confirm sprint completion"
            >
              Confirm
            </Button>
          </Box>
        </Box>
                   </Dialog>

                              {/* Complete Sprint Confirmation Modal */}
                 <Dialog
                   open={completeSprintModalOpen}
                   onClose={handleCancelCompleteSprint}
                   maxWidth="sm"
                   fullWidth
                   PaperProps={{
                     sx: {
                       borderRadius: 3
                     }
                   }}
                 >
                   <DialogTitle sx={{
                     borderBottom: 1,
                     borderColor: 'divider',
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center'
                   }}>
                     <Typography variant="h6" fontWeight={600}>
                       Complete Sprint: {sprintToComplete?.name}
                     </Typography>
                     <IconButton onClick={handleCancelCompleteSprint}>
                       <CloseIcon />
                     </IconButton>
                   </DialogTitle>
         
                   <Box sx={{ p: 3 }}>
                     <Typography variant="body2" color="text.secondary">
                       Are you sure you want to mark this sprint as completed? This action cannot be undone.
                     </Typography>
                     <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                       <Button
                         variant="outlined"
                         onClick={handleCancelCompleteSprint}
                       >
                         Cancel
                       </Button>
                       <Button
                         variant="contained"
                         onClick={handleConfirmCompleteSprint}
                       >
                         Confirm
                       </Button>
                     </Box>
                           </Box>
      </Dialog>

      {/* View Sprint Details Modal */}
      <Dialog
        open={viewDetailsModalOpen}
        onClose={handleCloseViewDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleCloseViewDetails();
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Sprint Details: {sprintToView?.name}
          </Typography>
          <IconButton 
            onClick={handleCloseViewDetails}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCloseViewDetails();
              }
            }}
            aria-label="Close sprint details"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ p: 3 }}>
          {sprintToView && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Sprint Overview */}
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {sprintToView.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {getStatusChip(sprintToView.status)}
                    <Chip 
                      label={`${sprintToView.tickets} tickets`} 
                      color="info" 
                      size="small" 
                    />
                  </Box>
                  {sprintToView.goal && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Goal:</strong> {sprintToView.goal}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Start:</strong> {sprintToView.start}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>End:</strong> {sprintToView.end}
                  </Typography>
                </Box>
              </Box>

              {/* Progress Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={getProgressValue(sprintToView)}
                      color={getProgressColor(sprintToView)}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: sprintToView.status === 'COMPLETED' ? 'grey.300' : 'grey.200'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {getProgressValue(sprintToView).toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getProgressLabel(sprintToView)}
                </Typography>
              </Box>

              {/* Mock Tickets Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Tickets in Sprint
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: 1, 
                  borderColor: 'divider',
                  minHeight: 100
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {sprintToView.tickets > 0 
                      ? `${sprintToView.tickets} tickets assigned to this sprint`
                      : 'No tickets assigned to this sprint yet'
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Ticket details would be displayed here in a real implementation
                  </Typography>
                </Box>
              </Box>

              {/* Sprint Timeline */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Sprint Timeline
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TimelineIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      <strong>Created:</strong> {sprintToView.start}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {sprintToView.status}
                    </Typography>
                    {sprintToView.status === 'COMPLETED' && (
                      <Typography variant="body2" color="success.main">
                        <strong>Completed:</strong> {sprintToView.end}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Delete Sprint Confirmation Modal */}
      <Dialog
        open={deleteSprintModalOpen}
        onClose={handleCancelDeleteSprint}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleCancelDeleteSprint();
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight={600} color="error.main">
            Delete Sprint
          </Typography>
          <IconButton 
            onClick={handleCancelDeleteSprint}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCancelDeleteSprint();
              }
            }}
            aria-label="Close delete confirmation"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <DeleteIcon color="error" sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                Are you sure you want to delete "{sprintToDelete?.name}"?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone. All sprint data will be permanently removed.
              </Typography>
              {sprintToDelete?.tickets > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  ⚠️ Warning: This sprint has {sprintToDelete.tickets} tickets assigned to it.
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancelDeleteSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCancelDeleteSprint();
                }
              }}
              aria-label="Cancel sprint deletion"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteSprint}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleConfirmDeleteSprint();
                }
              }}
              aria-label="Confirm sprint deletion"
            >
              Delete Sprint
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Row Actions Menu */}
                 <Menu
                   anchorEl={actionsMenuAnchor}
                   open={Boolean(actionsMenuAnchor)}
                   onClose={handleCloseActionsMenu}
                   PaperProps={{
                     sx: {
                       borderRadius: 2,
                       minWidth: 180,
                       boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                     }
                   }}
                 >
                   <MenuItem 
                     onClick={() => handleRowAction('edit')}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ' ') {
                         e.preventDefault();
                         handleRowAction('edit');
                       }
                     }}
                     aria-label="Edit sprint"
                   >
                     <AssignmentIcon sx={{ mr: 1.5, fontSize: 20 }} />
                     Edit Sprint
                   </MenuItem>
                   {selectedSprint?.status === 'ACTIVE' && (
                     <MenuItem 
                       onClick={() => handleRowAction('complete')}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter' || e.key === ' ') {
                           e.preventDefault();
                           handleRowAction('complete');
                         }
                       }}
                       aria-label="Complete sprint"
                     >
                       <TimelineIcon sx={{ mr: 1.5, fontSize: 20 }} />
                       Complete Sprint
                     </MenuItem>
                   )}
                   <MenuItem 
                     onClick={() => handleRowAction('view')}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ' ') {
                         e.preventDefault();
                         handleRowAction('view');
                       }
                     }}
                     aria-label="View sprint details"
                   >
                     <SearchIcon sx={{ mr: 1.5, fontSize: 20 }} />
                     View Details
                   </MenuItem>
                   <MenuItem 
                     onClick={() => handleRowAction('delete')}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ' ') {
                         e.preventDefault();
                         handleRowAction('delete');
                       }
                     }}
                     sx={{ color: 'error.main' }}
                     aria-label="Delete sprint"
                   >
                     <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
                     Delete Sprint
                   </MenuItem>
                 </Menu>
               </Dialog>
             );
           }

// ---------------- Enhanced Insights Section ----------------
function Insights({ tickets, sprints, members, trends, setTab }) {
  // Component state management
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalCount, setModalCount] = useState(0);
  const [activeSprintsModalOpen, setActiveSprintsModalOpen] = useState(false);
  
  // Debug useEffect to track re-renders
  React.useEffect(() => {
    // Component re-rendered with new tickets
  }, [tickets]);


  const handleCardClick = (title, count) => {
    if (title === "Active Sprints") {
      setActiveSprintsModalOpen(true);
    } else {
      setModalTitle(title);
      setModalCount(count);
      setModalOpen(true);
    }
  };

  const StatCard = ({ title, value, color = "primary", trend, subtitle, icon, onClick }) => {
    // Render StatCard component
    
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 1.5, 
          borderRadius: 1.5, 
          border: 1, 
          borderColor: "divider", 
          transition: "all 0.2s ease-in-out", 
          "&:hover": { transform: "translateY(-1px)", boxShadow: 3, borderColor: `${color}.main` },
          height: 119.33,
          width: 180,
          minWidth: 180,
          maxWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          position: 'relative',
          mx: 'auto',
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default'
        }}
        onClick={onClick}
      >
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          width: '100%', 
          height: '100%',
          alignItems: 'flex-start',
          pt: 0.5
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.5
          }}>
            <Avatar sx={{ 
              bgcolor: `${color}.light`, 
              color: `${color}.dark`, 
              width: 28, 
              height: 28,
              boxShadow: 1
            }}>
              {icon || <BoltIcon />}
            </Avatar>
          </Box>
          
          <Box sx={{ 
            flex: 1, 
            minWidth: 0, 
            display: 'flex', 
            flexDirection: 'column',
            pt: 0.5
          }}>
            <Typography 
              variant="body2" 
              color="text.primary" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '0.7rem', 
                whiteSpace: 'nowrap', 
                overflow: 'visible', 
                lineHeight: 1.1, 
                mb: 0.5 
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h6" 
              fontWeight={700} 
              color={`${color}.main`} 
              sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.1 
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                noWrap 
                variant="caption" 
                color="text.disabled" 
                sx={{ 
                  fontSize: '0.7rem' 
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ 
            minWidth: 56, 
            flexShrink: 0, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            pt: 0.5
          }}>
            <LinearProgress 
              variant="determinate" 
              value={60} 
              sx={{ 
                height: 6, 
                borderRadius: 999,
                mb: 0.5
              }} 
            />
            {trend && (
              <Typography 
                variant="caption" 
                color={trend.startsWith("↓") ? "error.main" : "success.main"} 
                sx={{ 
                  fontSize: '0.7rem',
                  textAlign: 'right'
                }}
              >
                {trend}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Stack spacing={4}>
                     {/* Enhanced Stat Cards - Single Row of 4 */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Total Tickets" 
              value={tickets.length} 
              subtitle="All time" 
              color="primary"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("Total Tickets", tickets.length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="To Do" 
              value={tickets.filter(t => t.status === "todo").length} 
              subtitle="Pending tasks" 
              color="warning"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("To Do", tickets.filter(t => t.status === "todo").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="In Progress" 
              value={tickets.filter(t => t.status === "inprogress").length} 
              subtitle="Active work" 
              color="info"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("In Progress", tickets.filter(t => t.status === "inprogress").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Done" 
              value={tickets.filter(t => t.status === "done").length} 
              subtitle="Completed" 
              color="success"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("Done", tickets.filter(t => t.status === "done").length)}
            />
          </Grid>
        </Grid>

        {/* Due Tickets - Second Row */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Due Tickets" 
              value={tickets.filter(t => t.dueDate && new Date(t.dueDate) < new Date()).length} 
              subtitle="Overdue" 
              color="error"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("Due Tickets", tickets.filter(t => t.dueDate && new Date(t.dueDate) < new Date()).length)}
            />
          </Grid>
        </Grid>

        {/* Third Row - 4 proper metric cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Active Sprints" 
              value={sprints.filter(s => s.active).length} 
              subtitle="Current" 
              color="info"
              icon={<TimelineIcon />}
              onClick={() => handleCardClick("Active Sprints", sprints.filter(s => s.active).length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Backlog" 
              value={tickets.filter(t => t.status === "backlog").length} 
              subtitle="Pending" 
              color="warning"
              icon={<BacklogIcon />}
              onClick={() => handleCardClick("Backlog", tickets.filter(t => t.status === "backlog").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Task" 
              value={tickets.filter(t => t.type === "task").length} 
              subtitle="Tasks" 
              color="primary"
              icon={<AssignmentIcon />}
              onClick={() => handleCardClick("Task", tickets.filter(t => t.type === "task").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <StatCard 
              title="Bug" 
              value={tickets.filter(t => t.type === "bug").length} 
              subtitle="Bugs" 
              color="error"
              icon={<BoltIcon />}
              onClick={() => handleCardClick("Bug", tickets.filter(t => t.type === "bug").length)}
            />
          </Grid>
        </Grid>

        {/* Fourth Row - 3 cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <StatCard 
              title="Story" 
              value={tickets.filter(t => t.type === "story").length} 
              subtitle="User Stories" 
              color="success"
              icon={<AssignmentIcon />}
              onClick={() => handleCardClick("Story", tickets.filter(t => t.type === "story").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <StatCard 
              title="Epic" 
              value={tickets.filter(t => t.type === "epic").length} 
              subtitle="Epics" 
              color="secondary"
              icon={<AssignmentIcon />}
              onClick={() => handleCardClick("Epic", tickets.filter(t => t.type === "epic").length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <StatCard 
              title="Subtask" 
              value={tickets.filter(t => t.type === "subtask").length} 
              subtitle="Subtasks" 
              color="info"
              icon={<AssignmentIcon />}
              onClick={() => handleCardClick("Subtask", tickets.filter(t => t.type === "subtask").length)}
            />
          </Grid>
        </Grid>



      {/* Sprint Metrics */}
      <SprintMetrics />

                            {/* Trend Charts */}
         <Box sx={{ width: '100%' }}>
           <Box sx={{ width: '100%', mb: 3 }}>
             <TrendByStatusChart title="Trend by Status" data={trends} />
           </Box>
           <Box sx={{ width: '100%', mb: 3 }}>
             <TrendChart title="Trend by Type" data={trends} />
           </Box>
         </Box>

                              {/* Backlog Chart */}
        <BacklogChart title="Backlog" data={trends} />

      {/* Performance Metrics */}
      <PerformanceMetrics />

             {/* Member Workload Table */}
       <MemberWorkloadTable members={members} />

      

      {/* Export & Reports */}
      <ExportActions />

      {/* Ticket Details Modal */}
      <TicketDetailsModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        count={modalCount}
        tickets={tickets}
      />

      {/* Active Sprints Modal */}
      <ActiveSprintsModal 
        open={activeSprintsModalOpen}
        onClose={() => setActiveSprintsModalOpen(false)}
        sprints={sprints}
      />
    </Stack>
  );
}

function KanbanColumn({ title, items, onEditTicket, onDeleteTicket }) {
  return (
    <Paper elevation={0} sx={{ 
      p: { xs: 1, sm: 1.5, md: 1.5 }, 
      borderRadius: 3, 
      border: 1, 
      borderColor: "divider", 
      bgcolor: "grey.50", 
      minHeight: { xs: 150, sm: 180, md: 200 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography fontWeight={600} variant="body2">{title} ({items.length})</Typography>
        <Button size="small" variant="outlined" startIcon={<BoltIcon />}>Refresh</Button>
      </Stack>
      <Stack spacing={1.5} sx={{ 
        maxHeight: { xs: 400, sm: 480, md: 520 }, 
        overflow: "auto", 
        pr: 0.5, 
        minHeight: { xs: 80, sm: 90, md: 100 },
        scrollBehavior: 'smooth',
        flex: 1,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(156, 163, 175, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(156, 163, 175, 0.4)',
          }
        }
      }}>
        {items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center", color: "text.secondary", borderStyle: "dashed" }}>
            <Typography variant="body2">No {title.toLowerCase()} items</Typography>
            <Typography variant="caption">Drag and drop tasks here</Typography>
          </Paper>
        ) : (
          items.map((t) => (
            <Paper key={t.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", transition: "all 0.2s ease-in-out", "&:hover": { transform: "translateY(-1px)", boxShadow: 1 } }}>
              <Typography variant="caption" color="text.secondary">#{t.id}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{t.summary || t.title || "-"}</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                {t.ticketType === "Bug" ? (
                  <Chip label="BUG" color="error" size="small" />
                ) : (
                  <Chip label={t.ticketType?.toUpperCase() || "TASK"} color="info" size="small" />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <AssigneeAvatars users={[t.assignee].filter(Boolean)} />
              </Stack>
              
              {/* Action buttons */}
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditTicket(t);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Are you sure you want to delete this ticket?')) {
                      onDeleteTicket(t.id);
                    }
                  }}
                  sx={{ p: 0.5, color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))
        )}
      </Stack>
    </Paper>
  );
}

// Board component moved to Board.jsx

// Helper function to safely parse JSON strings
const parseJsonField = (field) => {
  if (!field || typeof field !== 'string') return [];
  try {
    return JSON.parse(field);
  } catch (error) {
    console.warn('Failed to parse JSON field:', field, error);
    return [];
  }
};

// Main App Component
const App = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get the last selected tab from localStorage, default to 'Board' if none exists
  const getInitialTab = () => {
    try {
      const savedTab = localStorage.getItem('selectedTab');
      return savedTab || 'Board';
    } catch (error) {
      console.warn('Failed to get tab from localStorage:', error);
      return 'Board';
    }
  };
  
  const [tab, setTab] = useState(getInitialTab);
  const [tickets, setTickets] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [members, setMembers] = useState([]);
  const [trends, setTrends] = useState({ daily: [], weekly: [] });
  const [timeRange, setTimeRange] = useState('Last 30 days');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Save tab to localStorage whenever it changes
  const handleTabChange = (newTab) => {
    setTab(newTab);
    try {
      localStorage.setItem('selectedTab', newTab);
    } catch (error) {
      console.warn('Failed to save tab to localStorage:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ticketsData, sprintsData, membersData, trendsData] = await Promise.all([
        apiService.fetchTickets().catch(() => []), // Fallback to empty array
        apiService.fetchSprints().catch(() => []),
        apiService.fetchTeamMembers().catch(() => []),
        apiService.fetchTrends().catch(() => ({ daily: [], weekly: [] }))
      ]);
      
      setTickets(ticketsData);
      setSprints(sprintsData);
      setMembers(membersData);
      setTrends(trendsData);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      const errorSeverity = getErrorSeverity(err);
      setError(errorMessage);
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: errorSeverity 
      });
      logError(err, 'loadData');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadData();
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      // Map form data to API format - ensure all values are properly formatted
      const apiData = {
        type: ticketData.ticketType || 'task',
        title: ticketData.summary || '',
        description: ticketData.description || '',
        status: 'todo', // Default status
        priority: ticketData.priority || 'medium',
        assignee: ticketData.assignee || '',
        reporter: ticketData.reporter?.label || ticketData.reporter || '',
        dueDate: ticketData.dueDateTime ? ticketData.dueDateTime.toISOString() : null,
        epic: ticketData.epic || '',
        labels: Array.isArray(ticketData.labels) ? ticketData.labels : [],
        project_id: 1, // Default project ID
        storyPoints: ticketData.storyPoints || 0,
        sprint: ticketData.sprint || 'backlog'
      };
      
      // Remove any undefined or null values that might cause JSON issues
      Object.keys(apiData).forEach(key => {
        if (apiData[key] === undefined || apiData[key] === null) {
          delete apiData[key];
        }
      });
      
      console.log('Sending ticket data:', apiData); // Debug log
      
      // Test JSON serialization
      try {
        const jsonString = JSON.stringify(apiData);
        console.log('JSON stringified data:', jsonString); // Debug log
      } catch (jsonError) {
        console.error('JSON serialization error:', jsonError);
        console.error('Problematic data:', apiData);
        throw new Error('Invalid data format: ' + jsonError.message);
      }
      
      const newTicket = await apiService.createTicket(apiData);
      
      setTickets(prev => [...prev, newTicket]);
      setSnackbar({ open: true, message: 'Ticket created successfully!', severity: 'success' });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorSeverity = getErrorSeverity(error);
      setSnackbar({ open: true, message: errorMessage, severity: errorSeverity });
      logError(error, 'handleCreateTicket');
    }
  };

  const handleUpdateTicket = async (updatedTicket) => {
    try {
      const result = await apiService.updateTicket(updatedTicket.id, updatedTicket);
      setTickets(prev => prev.map(t => t.id === updatedTicket.id ? result : t));
      setSnackbar({ open: true, message: 'Ticket updated successfully!', severity: 'success' });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorSeverity = getErrorSeverity(error);
      setSnackbar({ open: true, message: errorMessage, severity: errorSeverity });
      logError(error, 'handleUpdateTicket');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await apiService.deleteTicket(ticketId);
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      setSnackbar({ open: true, message: 'Ticket deleted successfully!', severity: 'success' });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorSeverity = getErrorSeverity(error);
      setSnackbar({ open: true, message: errorMessage, severity: errorSeverity });
      logError(error, 'handleDeleteTicket');
    }
  };

  const handleEditTicket = (ticket) => {
    // In a real app, this would open a modal with ticket data
    // TODO: Implement ticket editing modal
  };

  const handleCreateNewTicket = () => {
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
  };

  const handleSaveTicket = async (ticketData) => {
    try {
      if (ticketData.id) {
        await handleUpdateTicket(ticketData);
      } else {
        await handleCreateTicket(ticketData);
      }
      setTicketModalOpen(false);
    } catch (error) {
      logError(error, 'handleSaveTicket');
      throw error; // Re-throw so CreateTicketModal can catch it
    }
  };

  const handleMoveTicket = async (ticketId, newStatus) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      if (!ticket) return;

      const updatedTicket = { ...ticket, status: newStatus };
      await apiService.updateTicket(ticketId, updatedTicket);
      setTickets(prev => prev.map(t => t.id === ticketId ? updatedTicket : t));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorSeverity = getErrorSeverity(error);
      setSnackbar({ open: true, message: errorMessage, severity: errorSeverity });
      logError(error, 'handleMoveTicket');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <LinearProgress sx={{ width: 200 }} />
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          </Stack>
        </Box>
      );
    }

    if (error) {
      return (
        <Paper sx={{ p: 3, mb: 3, bgcolor: "error.light", color: "error.contrastText" }}>
          <Typography variant="h6" gutterBottom>Error</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            variant="outlined" 
            sx={{ mt: 2, color: "error.contrastText", borderColor: "error.contrastText" }}
            onClick={() => setError(null)}
          >
            Dismiss
          </Button>
        </Paper>
      );
    }

    switch (tab) {
      case "Insights":
        return <Insights tickets={tickets} sprints={sprints} members={members} trends={trends} setTab={setTab} />;
              case "Board":
          return <Board 
            tickets={tickets} 
            onEditTicket={handleEditTicket}
            onDeleteTicket={handleDeleteTicket}
            onCreateTicket={handleCreateNewTicket}
            onMoveTicket={handleMoveTicket}
          />;
      case "List":
        return <div>List View - Coming Soon</div>;
      case "Calendar":
        return <div>Calendar View - Coming Soon</div>;
      case "Backlog":
        return <div>Backlog View - Coming Soon</div>;
      case "Sprints":
        return <SprintManagement />;
      case "All Tickets":
        return <AllTicketsView tickets={tickets} onEditTicket={handleEditTicket} onDeleteTicket={handleDeleteTicket} onCreateTicket={handleCreateNewTicket} />;
      case "Labels":
        return <div>Labels View - Coming Soon</div>;
      case "Workflow":
        return <div>Workflow View - Coming Soon</div>;
      case "Settings":
        return <Settings />;
      default:
        return <Board 
          tickets={tickets} 
          onEditTicket={handleEditTicket}
          onDeleteTicket={handleDeleteTicket}
          onCreateTicket={handleCreateNewTicket}
          onMoveTicket={handleMoveTicket}
        />;
    }
  };

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <DndProvider backend={HTML5Backend}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider theme={theme}>
          <DashboardLayout
            currentTab={tab}
            onTabChange={handleTabChange}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
            onRefresh={handleRefresh}
            isLoading={isLoading}
            onNewTicket={handleCreateNewTicket}
          >
            {renderContent()}
            
            {/* FAB */}
            <Fab 
              color="primary" 
              size="small"
              onClick={handleCreateNewTicket}
              sx={{ 
                position: "fixed", 
                bottom: 24, 
                right: 24,
                width: 48,
                height: 48
              }}
            >
              <AddIcon fontSize="small" />
            </Fab>

            {/* Ticket Creation/Editing Modal */}
            <CreateTicketModal
              open={ticketModalOpen}
              onClose={handleCloseTicketModal}
              onSave={handleSaveTicket}
            />

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity={snackbar.severity} 
                sx={{ width: '100%' }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </DashboardLayout>
          </ThemeProvider>
        </LocalizationProvider>
      </DndProvider>
    </ErrorBoundary>
  );
};

export default App;
